import React, { FC, useEffect, useState, useRef } from 'react';
import useModuleAccess from '../../hooks/useModuleAccess';
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Box, Paper, TextField, Grid, Drawer, CircularProgress, InputAdornment, Divider, Autocomplete, Button, MenuItem, Select, IconButton, ToggleButton, Radio, RadioProps, Skeleton, Card, Typography, Checkbox, FormControlLabel, DialogTitle } from '@mui/material';
import greyDialogTitleSx from '../Dialog/greyDialogTitleSx';
import { makeStyles } from "@material-ui/core/styles";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import DialogClientForm from '../Dialog/CreateClientForm';
import DialogDocumentSettingForm from '../../components/DialogDocumentSettingForm/DialogDocumentSettingForm';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchInvoiceSeqNumber, addInvoice, fetchInvoicesByInvNumber, fetchInvoiceById, updateInvoice, dispatchInvoice, fetchSelectedInvoicesLineItem, updateInvoiceStatus, fetchInvoicePreview } from '../../store/invoiceStore/invoiceThunk';
import { clearInvoiceDetails } from '../../store/invoiceStore/invoiceSlice';
import { fetchCounterPartyAccountById, fetchAccountsByName, fetchAccountSettings } from "../../store/accountThunk";
import DialogShareInvoiceReciept from '../DialogShareInvoiceReciept/DialogShareInvoiceReciept';
import axios from '../../utils/axios';
import { fetchDocumentSettings } from '../../store/documentSettingStore/documentSettingThunk';
import { fetchChargeLedgers } from '../../store/ledgerStore/ledgerThunk';
import { InvoiceFormInputs } from "../../utils/invoice.types";
import DocAttachInfo from '../DocumentAttachment/DocAttachInfo';
import TemplateSelect from './TemplateSelect';
import InvoicePreviewWithFilters from '../../pages/Invoice/InvoicePreviewWithFilters';
import TagSelect from './TagSelect';
import { CrmLeadValue } from '../Dialog/ClientAgent/Lead';
import { StyledBoxLoader } from './Invoice.style';
import { Utils, VocuherCategory, VocuherTypes } from "../../utils/common-utils";
import { buildAdditionalFieldsFromDefaults } from '../../utils/documentAdditionalFields';
import moment from 'moment';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import './Invoice.css';

import { fetchStates } from '../../store/stateStore/stateThunk';
import { fetchLocations } from '../../store/locationStore/locationThunk';
import { IOptions } from "../FormFields/CustomSelect";
import { experimentalStyled as styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import BuyerModel from './BuyerModel';
import InvoiceElements from './InvoiceElements';
import { InvoiceElementsInterface } from './InvoiceElementInterface';
import BankChanges from './BankChange';
import SplitPaymentDialog, { SplitPaymentLine } from './SplitPaymentDialog';
import TableInlineEdit3 from './InvoicelineItems';
import { fetchGstOptions, fetchTaxRates } from '../../store/gstStore/gstThunk';
import { ApiStatus } from "../../utils/api.types";
import { buildAuditChanges } from "../../utils/auditChanges";
import LoadingButton from '@mui/lab/LoadingButton';

import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';

// import { usePrompt } from '../../hooks/usePrompt';


// Nicer labels for common invoice fields (auto-labelling covers the rest).
const INVOICE_AUDIT_TOP_FIELDS: Record<string, string> = {
  invoiceNumber: 'Invoice Number', poNumber: 'PO Number', quotationNumber: 'Quotation Number',
  paymentTerms: 'Payment Terms', placeOfSupply: 'Place of Supply',
  paymentMode: 'Payment Mode', receivedAmount: 'Received Amount',
  date: 'Date', invoiceExpiryDate: 'Due Date', payBydate: 'Pay By Date',
  quotationDate: 'Quotation Date', poDate: 'PO Date',
};
const LINE_ITEM_AUDIT_FIELDS: Record<string, string> = {
  name: 'Name', description: 'Description', hsnCode: 'HSN/SAC', mrp: 'MRP',
  appliedRate: 'Price/Item', qty: 'Qty', discount: 'Discount', discountPercentage: 'Discount %',
};

// Fields the form recalculates on every save — these are derived, never a user edit,
// so they're excluded from the audit trail even in "audit everything" mode.
const INVOICE_DERIVED_KEYS = [
  'taxableAmount', 'principalAmount', 'amount', 'totalAmount', 'totalPayableAmount',
  'finalAmount', 'balanceAmount', 'balanceAmt', 'openingBalance',
  'tax1', 'tax2', 'tax3', 'finalTax', 'taxAmount', 'igstAmount', 'cgstAmount', 'sgstAmount',
  'igstPercentage', 'cgstPercentage', 'sgstPercentage', 'appliedIgstTaxRates',
  'appliedCgstTaxRates', 'appliedSgstTaxRates', 'appliedCgstSgstTaxRates', 'isTaxTypeIgst',
  'taxType', 'invoiceNumberInt', 'quotationCode', 'poCode', 'journalEntry', 'signature',
  'account', 'client', 'buyer', 'supplier', 'agent', 'billingAddress', 'shippingAddress',
  'pdfTemplate', 'invoiceAccountRelation', 'voucherCategory', 'voucherType', 'currency',
  'refInvoiceId', 'refInvoiceIds', 'refInvoiceNumber', 'status', 'fullyPaid',
  // Structural / handled-elsewhere or empty scaffolding fields:
  'items', 'lineItems', 'lineItem', 'subTotal', 'clientAddress', 'bankDetails',
  'additionalCharges', 'additionalFields', 'tcs', 'tds', 'roundOff', 'discount',
  'payment', 'payTo', 'termsContitons', 'emailSubject', 'emailText',
  // `terms` is the flattened text of termsContitons — already audited via flattenText,
  // so skip the raw top-level copy to avoid a duplicate "Terms" entry.
  'terms',
];

// Flatten the pay-to bank block into readable scalar fields so a bank change shows
// up as e.g. "Bank — Account Number: 123 → 456" instead of a JSON dump.
const flattenBank = (inv: any): Record<string, any> => {
  const b = inv?.payTo?.copyBankAccountDetails || inv?.payTo || {};
  return {
    bankAccountNumber: b.accountNumber,
    bankIFSC: b.IFSC,
    bankName: b.bankName,
    bankAccountName: b.accountName,
    bankBranch: b.branchName,
  };
};
const BANK_AUDIT_FIELDS: Record<string, string> = {
  bankAccountNumber: 'Bank — Account Number', bankIFSC: 'Bank — IFSC',
  bankName: 'Bank — Name', bankAccountName: 'Bank — Account Holder', bankBranch: 'Bank — Branch',
};

// Some invoice fields are stored as { active, text } objects. Flatten their text so a
// change shows as plain text instead of being skipped / dumped as JSON.
const flattenText = (inv: any): Record<string, any> => {
  const t = (v: any) => (v && typeof v === 'object' ? v.text : v);
  return {
    notesText: t(inv?.notes),
    subjectText: t(inv?.emailSubject),
    emailBodyText: t(inv?.emailText),
    termsText: t(inv?.termsContitons) ?? inv?.terms,
  };
};
const TEXT_AUDIT_FIELDS: Record<string, string> = {
  notesText: 'Notes', subjectText: 'Subject', emailBodyText: 'Email Text', termsText: 'Terms',
};

const computeAuditChanges = (original: any, updated: any) => {
  const main = buildAuditChanges(original, updated, INVOICE_AUDIT_TOP_FIELDS, {
    auditAll: true,
    derivedKeys: INVOICE_DERIVED_KEYS,
    arrays: [{
      key: 'lineItems',
      itemFields: LINE_ITEM_AUDIT_FIELDS,
      rowLabel: (item: any, idx: number) => item?.name || `Item ${idx + 1}`,
    }],
  });
  // Bank details (nested under payTo) — diff separately as flat fields.
  const bank = buildAuditChanges(flattenBank(original), flattenBank(updated), BANK_AUDIT_FIELDS);
  // Text fields stored as { active, text } (Notes, Subject, Email Text, Terms).
  const text = buildAuditChanges(flattenText(original), flattenText(updated), TEXT_AUDIT_FIELDS);

  // Documents: attachments is an array of ids; report count change (added/removed docs).
  const extra: Array<{ field: string; oldValue: any; newValue: any }> = [];
  const oldDocs = Array.isArray(original?.attachments) ? original.attachments.length : 0;
  const newDocs = Array.isArray(updated?.attachments) ? updated.attachments.length : 0;
  if (oldDocs !== newDocs) {
    extra.push({ field: newDocs > oldDocs ? 'Document attached' : 'Document removed', oldValue: `${oldDocs} document(s)`, newValue: `${newDocs} document(s)` });
  }

  return [...main, ...bank, ...text, ...extra];
};

// Schema to validate account form.
const schema = yup.object().shape({
  invoiceNumber: yup.string().required("Invoice is required"),
  client: yup.string().required("Please select Client"),
  account: yup.string().required("Account is required."),
  placeOfSupply: yup.string().required("Please select place of supply"),
  lineItem: yup.string().required("Minimum 1 Line Item required.")
});

// credit/debit note reason options (must match backend enum)
const REASON_OPTIONS: IOptions[] = [
  { label: 'Sales Return', value: 'SALES_RETURN' },
  { label: 'Price Revision', value: 'PRICE_REVISION' },
  { label: 'Discount', value: 'DISCOUNT' },
  { label: 'Deficiency in Service', value: 'DEFICIENCY_IN_SERVICE' },
  { label: 'Order Cancellation', value: 'ORDER_CANCELLATION' },
  { label: 'Other', value: 'OTHER' },
];

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#e3f2fd' : '#e3f2fd8c',
  ...theme.typography.body2,
  padding: theme.spacing(2),
  textAlign: 'left',
  color: '#e3f2fd',
}));

const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: '50%',
  width: 16,
  height: 16,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 0 0 1px rgb(16 22 26 / 40%)'
      : 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundColor: theme.palette.mode === 'dark' ? '#394b59' : '#f5f8fa',
  backgroundImage:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))'
      : 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: theme.palette.mode === 'dark' ? '#30404d' : '#ebf1f5',
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background:
      theme.palette.mode === 'dark' ? 'rgba(57,75,89,.5)' : 'rgba(206,217,224,.5)',
  },
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#137cbd',
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&:before': {
    display: 'block',
    width: 16,
    height: 16,
    backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#106ba3',
  },
});

// Inspired by blueprintjs
function BpRadio(props: RadioProps) {
  return (
    <Radio
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      {...props}
    />
  );
}

interface InvoiceProps {
  type: string;
}

interface AdditionalCharge {
  id: number;
  title: string;
  value: number;
  ledgerId?: string;
  ledgerName?: string;
  accountingCode?: string;
  isTaxable?: boolean;
  taxPercentage?: number;
  taxAmount?: number;
  totalWithTax?: number;
}

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    boxShadow: 'none',
    borderRadius: 7
  },
  toggleButtonGroup: {
    width: '100%',
  },
  toggleButton: {
    width: 15,
    height: 15,
    marginBottom: 2
  },

});

// Invoice component. 
const Invoice: FC<InvoiceProps> = ({ type }) => {


  const location = useLocation();
  const { pathname } = location;
  // Set by Save & View: the invoice just saved, shown in the drawer over a fresh form.
  const savedView: any = location.state || {};
  const { vType, invoiceId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamVoucherType = searchParams.get("_reftype") || null;
  const searchParamRefInvoiceId = searchParams.get("_ref") || null;
  const searchParamRefMultipleInvoiceIds = searchParams.get("_refMultiple") || null;
  const searchParamCopyFlag = searchParams.get("_copy") || false;
  const isEditing = invoiceId ? true : false;

  let voucherType = '';
  let pageTitle = '';

  if (vType) {
    const invoiceMeta = Utils.getInvoiceRouteName(vType);
    voucherType = invoiceMeta.voucherType;
    pageTitle = invoiceMeta.pageTitle;
  }

  // tanmay -----xxxx

  interface BuyerProps {
    name: string;
  }

  const [isDirty, setIsDirty] = useState(false);


  const [invoice, setInvoice] = useState<InvoiceElementsInterface | any>(InvoiceElements);
  const { status } = useAppSelector(state => state.invoices);
  const ocrBlocked = useModuleAccess().planDenial('ocr', 'create');
  const [statevalue, statevaluechange] = useState(null)
  // const [invoice, setInvoice] = useState<InvoiceFormInputs>();
  const [invoiceOptions, setInvoiceOptions] = useState<any[]>([]);
  const [buyer, setBuyer] = useState<BuyerProps[]>([]);
  const [buyerModel, setBuyerModel] = useState(false);
  const [bankModel, setBankModel] = useState(false);
  const classes = useStyles();
  const scrollRefFooter = useRef<null | HTMLDivElement>(null);
  const [gstOptions, setGstOptions] = useState<any[]>([]);
  const [calculations, setCalculations] = useState<any>(null);
  const [taxRateOptions, setTaxRateOptions] = useState<any>();
  const [tdsTaxRateOptions, setTdsTaxRateOptions] = useState<any[]>([]);
  const [tcsTaxRateOptions, setTcsTaxRateOptions] = useState<IOptions[]>([]);
  const [tcstype, setTcsType] = useState(1);

  const [inLineItems, setInLineItems] = useState<any[]>([]);
  const [payToObj, setPayToObj] = useState<any>();
  const [editInLineItems, setEditInLineItems] = useState<any[]>([]);
  // Branch the invoice belongs to; independent of where the items are picked from.
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');

  const [invoiceCalculation, setInvoiceCalculation] = useState<any>({});
  const [lineItemSummary, setLineItemSummary] = useState<any>({});
  const [balanceAmount, setBalanceAmount] = useState(0);
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [splitPayments, setSplitPayments] = useState<SplitPaymentLine[]>([]);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const splitSnapshot = useRef<any>(null);

  const [tcsSelected, setTcsSelected] = useState({
    _id: "",
    label: "",
    rate: 0
  })
  const [tdsSelected, setTdsSelected] = useState({
    _id: "",
    label: "",
    rate: 0
  })
  const [additionalChargesSum, setAdditionalChargesSum] = useState<Number>(0);
  const [flatDiscount, setFlatDiscount] = useState('');
  const [flatOn, setFlatOn] = useState(false);
  const [savedFlat, setSavedFlat] = useState(0);
  const [flatPct, setFlatPct] = useState(false);
  // Flat discount in ₹; a percent is taken on the items total before the discount.
  const flatBase = (inLineItems || []).reduce((n: number, r: any) => n + Number((r._preFlat || r).total || 0), 0);
  const flatAmt = !flatOn ? 0 : flatPct ? Number(((flatBase * (Number(flatDiscount) || 0)) / 100).toFixed(2)) : Number(flatDiscount) || 0;
  const [chargeLedgers, setChargeLedgers] = useState<any[]>([]);

  const dispatch = useAppDispatch();

  const handleTcsClick = (index: number) => {
    setTcsType(index);
  };

  // window.onbeforeunload = function() {
  //   return is ? "If you leave this page you will lose your unsaved changes." : null;
  // }


  useEffect(() => {
    if (invoice && invoice.notes.active | invoice.termsContitons.active) {
      scrollRefFooter?.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [invoice.notes.active, invoice.termsContitons.active]);

  const buyerList = async () => {
    await dispatch(fetchAccountsByName('')).unwrap().then((data) => {
      if (data) {
        const d = data.accounts.filter(function (item) {
          return item._id !== selectedAccount._id;
        });
        setBuyer(d);
      }
    })
  }

  // const getSelectedState = async (stateId:String) => {
  //   if (stateId) {
  //     const d = stateOptions.filter(function (item) {
  //       if (item.value == stateId ) {
  //         return { label: item.name, value: item._id }
  //       }
  //     });
  //     setPlaceOfSupply(d);
  //   }
  // }


  useEffect(() => {

    let isMounted = true;

    // check if this function call is required or not
    const getGstOptions = async () => {
      dispatch(fetchGstOptions()).unwrap().then(async (data) => {
        if (data) {
          setGstOptions(data);
        }
      });
    }

    const getStateOptions = async () => {
      dispatch(fetchStates()).unwrap().then(async (data) => {
        if (data) {
          if (data.length > 0) {
            setStateOptions(data.map((item: any) => ({ label: item.name, value: item._id })));
          }
        }
      });
    }

    const getTdsTcsTaxRates = async () => {
      dispatch(fetchTaxRates()).unwrap().then(async (data) => {
        if (data) {
          if (data.length > 0) {
            setTaxRateOptions(data)
            let tdsRates = data.filter((items) => items.taxType == "tds");
            let tcsRates = data.filter((items) => items.taxType == "tcs");

            const toOption = (item: any) => ({
              label: `${item.taxRate}% - ${item.taxSection} ${item.taxName}`,
              value: item._id
            });
            setTdsTaxRateOptions(tdsRates.map(toOption));
            setTcsTaxRateOptions(tcsRates.map(toOption));
          }
        }
      });
    }

    getGstOptions()
    getStateOptions()
    getTdsTcsTaxRates()
    // buyerList();

    // Fetch ledgers for additional charges based on transaction type
    const ledgerType = type === VocuherCategory.SALE ? 'income' : 'expense';
    dispatch(fetchChargeLedgers(ledgerType as 'income' | 'expense')).unwrap().then((data) => {
      if (data) {
        setChargeLedgers(data);
      }
    }).catch((error) => {
      console.error('Error fetching charge ledgers:', error);
    });

    dispatch(fetchLocations());

    return () => {
      isMounted = false;
    };

  }, [type, dispatch]);

  // const selectedState = React.useMemo(
  //   () => stateOptions.filter((v) => v.selected),
  //   [stateOptions],
  // );

  useEffect(() => {
    console.log("additionalChargesSum called ==== ");
    let initialSum = 0;
    let _additionalCharges = 0;
    let _additionalChargesTax = 0;
    console.log(invoice.additionalCharges.charges);
    if (invoice.additionalCharges.charges.length > 0) {
      // Calculate base amount (without tax)
      _additionalCharges = invoice.additionalCharges.charges.reduce(
        (previousValue: any, currentValue: any) => previousValue + Number(currentValue.value),
        initialSum
      );

      // Calculate total tax from additional charges
      _additionalChargesTax = invoice.additionalCharges.charges.reduce(
        (previousValue: any, currentValue: any) => {
          if (currentValue.isTaxable && currentValue.taxAmount) {
            return previousValue + Number(currentValue.taxAmount);
          }
          return previousValue;
        },
        initialSum
      );
    }

    console.log("--------- _additionalCharges ==> ", _additionalCharges);
    console.log("--------- _additionalChargesTax ==> ", _additionalChargesTax);

    // Store both the base amount and total with tax
    setAdditionalChargesSum(_additionalCharges + _additionalChargesTax);
  }, [invoice])

  useEffect(() => {
    console.log("========== lineItemSummary, TDS/TCS  useEffect Called ======== ");
    console.log(lineItemSummary.data);
    console.log(lineItemSummary.values);

    if (!isLoading && lineItemSummary.data && lineItemSummary.values && lineItemSummary.data.length > 0) {

      // var data = lineItemSummary;
      let s = { ...invoice };
      // let initialSum = 0;
      // let _additionalCharges = s.additionalCharges.charges.reduce((previousValue:any, currentValue:any) => previousValue + currentValue.value, initialSum);
      // console.log("--------- ==> ", _additionalCharges);
      let totalAmount = lineItemSummary.values.finalAmount;
      let taxableAmount = lineItemSummary.values.amount;
      var tcsRate = tcsSelected.rate;
      var tdsRate = tdsSelected.rate;
      var tcsAmount = (tcsRate * totalAmount) / 100;
      var tdsAmount = (tdsRate * taxableAmount) / 100;
      console.log(totalAmount, tcsAmount, additionalChargesSum);
      var _totalAmount = totalAmount + tcsAmount + additionalChargesSum;
      var _totalPayableAmount = (tdsAmount > 0) ? (totalAmount + tcsAmount + additionalChargesSum) - tdsAmount : 0;

      var _taxType = lineItemSummary.values.taxType
      var _taxTotal = lineItemSummary.values.tax
      console.log(_taxTotal)
      console.log(_taxType)

      var _sgsttotal = _taxType ? 0 : _taxTotal / 2;
      var _cgsttotal = _taxType ? 0 : _taxTotal / 2;
      var _igsttotal = _taxType ? _taxTotal : 0;

      console.log(_sgsttotal, _cgsttotal, _igsttotal)

      // Calculate Final Amount (rounded total without decimals)
      var _amountBeforeRounding = _totalPayableAmount > 0 ? _totalPayableAmount : _totalAmount;
      var _finalAmount = Math.round(_amountBeforeRounding);
      var _roundOff = _finalAmount - _amountBeforeRounding;

      let calculated = {
        items: lineItemSummary.data,
        taxableAmount: lineItemSummary.values.amount,
        taxType: _taxType,
        igstAmount: _igsttotal,
        cgstAmount: _cgsttotal,
        sgstAmount: _sgsttotal,
        igstPercentage: lineItemSummary.values.appliedIgstTaxRates,
        // taxPercentage: String((lineItemSummary.values.tax / lineItemSummary.values.amount) * 100).slice(0,4),
        cgstPercentage: lineItemSummary.values.appliedCgstSgstTaxRates,
        sgstPercentage: lineItemSummary.values.appliedCgstSgstTaxRates,
        totalAmount: Number(_totalAmount).toFixed(2),
        totalPayableAmount: Number(_totalPayableAmount),
        finalAmount: _finalAmount,
        roundOff: _roundOff,
        tcs: { totalAmount: Number(tcsAmount).toFixed(2) },
        tds: { totalAmount: Number(tdsAmount).toFixed(2) },
        appliedIgstTaxRates: lineItemSummary.values.appliedIgstTaxRates,
        appliedCgstSgstTaxRates: lineItemSummary.values.appliedCgstSgstTaxRates,

      }
      var _receivedAmount = 0;
      if (s.fullyPaid) {
        _receivedAmount = _finalAmount;
      } else {
        _receivedAmount = receivedAmount;
      }
      // Balance Amount now uses Final Amount (rounded)
      var _balanceAmount = _finalAmount - _receivedAmount;

      console.log(_receivedAmount);
      console.log('Balance Amount:', _balanceAmount);
      setReceivedAmount(_receivedAmount);
      setBalanceAmount(_balanceAmount)

      s.receivedAmount = _receivedAmount;
      s.balanceAmount = _balanceAmount
      try {
        s.additionalCharges.totalSum = additionalChargesSum;

        // Calculate total tax and total with tax for additional charges
        let totalTaxAmount = 0;
        let totalWithTax = 0;
        if (s.additionalCharges.charges && s.additionalCharges.charges.length > 0) {
          s.additionalCharges.charges.forEach((charge: any) => {
            if (charge.isTaxable && charge.taxAmount) {
              totalTaxAmount += Number(charge.taxAmount);
            }
            totalWithTax += Number(charge.value) + (charge.isTaxable ? Number(charge.taxAmount || 0) : 0);
          });
        }
        s.additionalCharges.totalTaxAmount = totalTaxAmount;
        s.additionalCharges.totalWithTax = totalWithTax;
      } catch (error) {
        console.log(error);
      }

      setInvoiceCalculation(calculated);
      // `s` is a stale copy; keep the latest tags so the line-item auto-select isn't overwritten.
      setInvoice((prev: any) => ({ ...s, tags: prev.tags }));
    } else {
      console.log("Empty lineItemSummary")
    }



    // const updateInLineFeild = async () => {
    //   if(inLineItems.length > 0) {
    //     console.log("========== inLineItems Found   ======== ");
    //     let s = {...invoice};
    //     let totalAmount = s.totalAmount
    //     var tcsRate = tcsSelected.rate | 0;
    //     var tdsRate = tdsSelected.rate | 0;
    //     var tcsAmount = (tcsRate * totalAmount) / 100 | 0;
    //     var tdsAmount = (tdsRate * totalAmount) / 100 | 0;
    //     s.totalAmount = totalAmount + tcsAmount;
    //     s.totalPayableAmount = (tdsAmount > 0) ? (totalAmount + tcsAmount ) - tdsAmount : 0;
    //     s.tcs.totalAmount = tcsAmount;
    //     s.tds.totalAmount = tdsAmount;
    //     setInvoice(s);
    //   }
    // }

    // updateInLineFeild();

    // if(inLineItems.length > 0) {
    //   console.log("========== inLineItems Found   ======== ");
    //   let s = {...invoice};
    //   let totalAmount = invoiceCalculation.totalAmount;
    //   var tcsRate = tcsSelected.rate | 0;
    //   var tdsRate = tdsSelected.rate | 0;
    //   var tcsAmount = (tcsRate * totalAmount) / 100 | 0;
    //   var tdsAmount = (tdsRate * totalAmount) / 100 | 0;
    //   s.totalAmount = totalAmount + tcsAmount;
    //   s.totalPayableAmount = (tdsAmount > 0) ? (totalAmount + tcsAmount ) - tdsAmount : 0;
    //   s.tcs.totalAmount = tcsAmount;
    //   s.tds.totalAmount = tdsAmount;
    //   setInvoice(s);
    // }
  }, [lineItemSummary, tcsSelected, tdsSelected, balanceAmount, receivedAmount, additionalChargesSum, invoice.additionalCharges.apply])

  const rowOnChangeLineItemHandler = (data: any) => {
    console.log("========== rowOnChangeLineItemHandler ======== ");
    console.log(data.values);
    console.log(data.data);

    if (data.values && data.data && data.data.length > 0) {
      setLineItemSummary(data);
      setInLineItems(data.data);
    }

  }

  const onChangeTcsHandler = (event: any, value: any) => {
    console.log("======= Called onChangeTcsHandler ====", event.target.value);
    console.log("======= Called onChangeTcsHandler ====", event);

    setTcsSelected({
      _id: "",
      label: "",
      rate: 0
    })

    if (!event.target.value) return; // "None" selected

    const d = taxRateOptions.filter(function (item: any) {
      console.log(item)
      return item._id == event.target.value;
    });

    console.log("d--------", d)

    setTcsSelected({
      _id: event.target.value,
      label: event.target.lable,
      rate: d[0].taxRate
    })
  }

  const onChangeTdsHandler = (event: any, value: any) => {
    console.log("======= Called onChangeTdsHandler ====", event.target.value);
    console.log("======= Called onChangeTdsHandler ====", event.target.label);

    setTdsSelected({
      _id: "",
      label: "",
      rate: 0
    })

    if (!event.target.value) return; // "None" selected

    const d = taxRateOptions.filter(function (item: any) {
      console.log(item)
      return item._id == event.target.value;
    });

    console.log("d--------", d)

    setTdsSelected({
      _id: event.target.value,
      label: event.target.lable,
      rate: d[0].taxRate
    })
  }

  const onChange = (e: any) => {
    const value = e.target.value > 0 ? e.target.value : 0;
    const name = e.target.name;
    // let discountPercentage = (name==='discountPercentage')? value: ;
    // let discountAmount = (name==='discountAmount')? value : invoice.pricePerItem;
    let _amount = invoice.taxableAmount;
    let _discountPercentage = (name === 'discountPercentage') ? value : invoice.discount.percentage;
    let _discountAmount = (name === 'discountAmount') ? value : invoice.discount.amount;
    let _taxPercentage = (name === 'taxPercentage') ? value : invoice.taxPercentage;

    let discountedAmount = (_amount * _discountPercentage) / 100;
    let discountedPercentage = _discountPercentage;

    if ((name === 'discountAmount') && _discountAmount && _discountAmount > 0) {
      // if (_discountAmount <= discountedAmount) {
      discountedPercentage = (_discountAmount / _amount) * 100;
      // } 
      // console.log('dis',discountedPercentage);
    }

    console.log(_amount, discountedAmount, discountedPercentage);

    // let taxedIGST = ((amount - (discountedAmount | 0)) * _taxPercentage)/100;
    // let finalAmt = amount - (discountedAmount | 0) + (taxedIGST | 0);
    setInvoice({
      ...invoice,
      discount: {
        apply: true,
        percentage: discountedPercentage | _discountPercentage,
        amount: discountedAmount | _discountAmount,
      }
    })
    // row.amount = Number(amount - (discountedAmount | 0));
    // row.finalAmount = Number(finalAmt);
    // row.igstAmount = Number(taxedIGST);
    // row.cgstAmount = Number(taxedIGST)/ 2;
    // row.sgstAmount = Number(taxedIGST)/ 2;
    // setRows(newRows);
  };

  const setInvoiceText = (value: any, field: string) => {
    let s = { ...invoice };
    s[field] = value;
    console.log(s);
    setInvoice(s);
  }

  // const handleChange = (event: SelectChangeEvent<typeof personName>) => {
  //   const {
  //     target: { value },
  //   } = event;
  //   setPersonName(
  //     // On autofill we get a stringified value.
  //     typeof value === 'string' ? value.split(',') : value,
  //   );
  // };

  // tanmay ------xxx



  // use form
  const methods = useForm<InvoiceFormInputs>({
    resolver: yupResolver(schema),
  });

  const names = ["CGST", "SGST", "IGST"]

  // States
  const [open, setOpen] = useState<Array<boolean>>([false, false, false, false]);
  const [valueDate, setValueDate] = useState<Date>(new Date());
  const [invoiceExpiryDate, setInvoiceExpiryDate] = useState<Date>(new Date(Date.now() + 15 * 24 * 60 * 60 * 1000));
  const [createDate, setCreateDate] = useState<Date>(new Date());
  const [quotationDate, setQuotationDate] = useState<Date>(new Date());
  const [poDate, setPoDate] = useState<Date>(new Date());
  const [payByDate, setPayByDate] = useState<Date>(new Date(Date.now()));
  const [invoiceSeqData, setInvoiceSeqData] = useState<any>();
  const [tagsMandatory, setTagsMandatory] = useState(false);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(!!savedView.savedInvoiceId && !savedView.share);
  const shareAfterSaveRef = useRef(false);
  const [shareDrawerOpen, setShareDrawerOpen] = useState(!!savedView.share);
  const [shareData, setShareData] = useState<any>(null);
  // WhatsApp "Invoice created" setting: when ON the backend already sends the invoice, so Save & Share is hidden.
  const [waInvoiceCreatedOn, setWaInvoiceCreatedOn] = useState<boolean | null>(null);
  const canSaveShare = !isEditing && waInvoiceCreatedOn === false && type !== VocuherCategory.PURCHASE && type !== 'account-payable';
  const canUpdateShare = isEditing && type !== VocuherCategory.PURCHASE && type !== 'account-payable';
  const invoiceDetails = useAppSelector(state => state.invoices.invoiceDetails);
  const { documentSettings } = useAppSelector(state => state.documentSettings);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { countries } = useAppSelector(state => state.country);
  const { countryStates } = useAppSelector(state => state.countryState);
  const { docs } = useAppSelector(state => state.docAttach);
  // The API already scopes this to the locations the user is allowed to access.
  const { locations } = useAppSelector(state => state.locations);

  const locationOptions = React.useMemo(
    () => (locations || [])
      .filter((l: any) => l.isActive !== false)
      .map((l: any) => ({ value: l._id, label: `${l.name} (${l.code})` })),
    [locations]
  );

  // Default to the first branch the user can reach rather than leaving it blank.
  useEffect(() => {
    if (locationOptions.length > 0 && !selectedLocationId) {
      setSelectedLocationId(locationOptions[0].value);
    }
  }, [locationOptions, selectedLocationId]);

  const selectedLocation = (locations || []).find((l: any) => l._id === selectedLocationId);

  const [docData, setDocData] = useState<any[]>([]);

  // Lead attribution picked in the buyer form's Lead section. Sale vouchers on Unofy
  // CRM accounts only. The sub-source becomes the invoice's `source`; the agent only rides along to the CRM.
  const [crmLead, setCrmLead] = useState<CrmLeadValue>({});
  const crmLeadEnabled = type === VocuherCategory.SALE && !!(selectedAccount as any)?.unofyCrm?.enabled;

  const [selectedAgent, setSelectedAgent] = useState<any>();
  const [isClientLoading, setIsClientLoading] = useState(false);
  const [isBuyerLoading, setIsBuyerLoading] = useState(false);
  const [isAgentLoading, setIsAgentLoading] = useState(false);
  // Create mode starts loading so the form mounts once, after the invoice number loads.
  const [isLoading, setIsLoading] = useState(!isEditing);
  const [isformLoaded, setIsFormLoaded] = useState(false);
  const [isBankChangeLoading, setIsBankChangeLoading] = useState(false);
  const [fetchData, setFetchData] = useState('');
  const [fetchDocs, setFetchDocs] = useState('');
  const [refreshDocs, setRefreshDocs] = useState(false);
  const [docDeleteId, setDocDeleteId] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("DRAFT");
  const [payToAccount, setPayToAccount] = useState<any>();
  const formRef = useRef<any>();
  const [isDispatched, setIsDispatched] = useState(false);
  const [queryParams, setQueryParams] = useState('');
  const [documentSettingId, setDocumentSettingId] = useState('');
  const [newInvoiceId, setNewInvoiceId] = useState('');

  const [placeOfSupply, setPlaceOfSupply] = useState(null);
  const [stateOptions, setStateOptions] = useState<IOptions[]>([]);

  const [selectedClient, setSelectedClient] = useState<any>();
  // Family member the invoice is for (null = the buyer themself).
  const [invoiceMember, setInvoiceMember] = useState<any>(null);
  // Money on a credit / debit note is a refund (the unpaid part of the original invoice is set off).
  const isNoteVoucher = voucherType == VocuherTypes.CREDIT_NOTE || voucherType == VocuherTypes.DEBIT_NOTE;
  // Age today from DOB when known, else the stored age.
  const ageOnInvoice = (m: any): number | undefined => {
    if (m?.dateOfBirth) {
      const dob = new Date(m.dateOfBirth);
      if (!isNaN(dob.getTime())) {
        const t = new Date();
        let y = t.getFullYear() - dob.getFullYear();
        if (t.getMonth() < dob.getMonth() || (t.getMonth() === dob.getMonth() && t.getDate() < dob.getDate())) y--;
        if (y >= 0) return y;
      }
    }
    return m?.age ?? undefined;
  };
  const [selectedClientBillToStateId, setSelectedClientBillToStateId] = useState<any>();
  const [selectedClientShipToStateId, setSelectedClientShipToStateId] = useState<any>();
  const [selectedAccountStateId, setSelectedAccountStateId] = useState<any>();
  const [isTaxTypeIgst, setIsTaxTypeIgst] = useState<boolean>(false); // default to cgst_sgst.  If true then igst
  // Only read by the company info section, which is commented out for now.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedAccountAddress, setSelectedAccountAddress] = useState<any>();
  // const [selectedClientStateOfRegistrationId, setSelectedClientStateOfRegistrationId] = useState<any>();
  const [selectedBuyer, setSelectedBuyer] = useState<any>();

  const [selectedInvoiceP, setSelectedInvoiceP] = useState<any>('');
  const [groupedLineItems, setGroupedLineItems] = useState<any>([]);
  const [refNote, setRefNote] = useState("");
  // const [selectedInvoiceP, setSelectedInvoiceP] = useState<any>('');

  const navigate = useNavigate();

  const openCloseDrawer = (label: string) => {
    let openArray = [...open]
    if (label === 'document') {
      openArray[0] = !openArray[0];
    } else if (label === 'client') {
      openArray[1] = !openArray[1];
      setIsClientLoading(true);
    } else if (label === 'agent') {
      openArray[2] = !openArray[2];
      setIsAgentLoading(true);
    } else {
      openArray[3] = !openArray[3];
      setIsClientLoading(true);
    }
    setOpen(openArray);
  }

  const closeDialog = (isSaved: boolean) => {
    let openArray = [...open]
    openArray.forEach((x, i) => openArray[i] = false);
    setOpen(openArray);

    if (isSaved) {
      setFetchData(Math.random().toString());
    }
  }

  const closeDoc = (isSaved: boolean) => {
    if (isSaved) {
      setRefreshDocs(true);
      setFetchDocs(Math.random().toString());
    }
  }

  const cancelDialog = () => {
    setIsClientLoading(false); setSelectedAgent(false); setIsAgentLoading(false); setIsLoading(false);
  }

  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue) {
      if (name === 'valueDate')
        setValueDate(newValue);
      else if (name === 'payBydate')
        setPayByDate(newValue);
      else if (name === 'quotationDate')
        setQuotationDate(newValue);
      else if (name === 'invoiceExpiryDate')
        setInvoiceExpiryDate(newValue);
      else if (name === 'poDate')
        setPoDate(newValue)
      else
        setCreateDate(newValue);
    }
  };

  const rowOnChangeHandler = (data: any) => {
    console.log("called row on change handler from table invoice 3");
    console.log(data);
    // setInLineItems(data);
  };

  const payToOnChangeHandler = (data: any) => {
    console.log("debug payto payToOnChangeHandler -======", data);
    console.log(data);
    const s = { ...invoice };
    s.bankDetails.active = false;
    setPayToObj(data);
    s.bankDetails.active = true;
    setInvoice(s);
  };

  const pdftemplateOnChangeHandler = (data: any, templateOption?: any) => {
    console.log("debug payto pdftemplateOnChangeHandler -======", data, templateOption);
    const s = { ...invoice };
    s.pdfTemplate = data;
    setInvoice(s);
  };

  // Tags carried by the picked SKUs; the invoice tag picker auto-applies a single common one.
  const lineItemTagIds = (inLineItems || []).flatMap((row: any) =>
    (row?.tags || []).map((t: any) => (typeof t === 'string' ? t : t?._id)).filter(Boolean));

  const tagsOnChangeHandler = (tagIds: string[]) => {
    const s = { ...invoice };
    s.tags = tagIds;
    setInvoice(s);
    setIsDirty(true);
  };

  const selectedClientHandler = (data: any) => {
    setSelectedClient(data);
    setInvoiceMember(data?._familyMember || null);
    // Lead section of the buyer form. Only the sub-source label is stored on the
    // invoice (as `source`); the agent is forwarded to the CRM, never saved here.
    if (data?.crmLead) setCrmLead(data.crmLead);
    setIsDirty(true);
  };

  const selectedAgentHandler = (data: any) => {
    setSelectedAgent(data);
  };

  // Method to handle save and send click events
  const selectedDispatchHandler = async (data: any) => {
    console.log("selectedDispatchHandler called 111111")
    let params = '';
    let currentValues = methods.getValues();
    Object.entries(data).forEach(([key, value]) => {
      if (value)
        params += key + '=' + value + '&';
    });
    setQueryParams(params.slice(0, -1))
    // if(invoiceId)
    //   await dispatch(dispatchInvoice({ invoiceId: invoiceId, params: params.slice(0, -1), templatePayload: { templateId: currentValues.pdfTemplate} })).then(async (data: any) => { if (data.payload) onCancel(); });
    // else {
    if (formRef) {
      console.log("selectedDispatchHandler formRef called 22222")
      setIsDispatched(true);
      formRef?.current.submit.click();
    }
    console.log("selectedDispatchHandler formRef skipped called 3333")
    // }
  };

  //Method to handle invoice form submit.
  const onSubmit = async (data: InvoiceElementsInterface, isDraft: boolean = false, split?: SplitPaymentLine[]) => {
    console.log("Called onSubmit 124141 ========= ", data);
    console.log("Is Draft Save:", isDraft);

    if (!selectedClient || selectedClient._id == '') {
      toast.error("Please Select Party");
      return;
    } else if (!inLineItems || (Array.isArray(inLineItems) ? inLineItems.length === 0 : Object.keys(inLineItems).length === 0)) {
      toast.error("Select atleast one line item from inventory");
      return;
    } else {
      // Validate that Price/Item and Amount are greater than 0 for all line items
      const lineItemsArray = Array.isArray(inLineItems) ? inLineItems : Object.values(inLineItems);
      const hasInvalidLineItem = lineItemsArray.some(
        (item: any) => Number(item.appliedRate) <= 0 || Number(item.amount) <= 0
      );

      if (hasInvalidLineItem) {
        toast.error("Please enter Price/Item rate");
        return;
      }
    }

    if (!selectedLocationId && locationOptions.length > 0) {
      toast.error("Please select a location");
      return;
    }

    // Credit/debit notes must state a reason: it decides whether stock & COGS are
    // restored (SALES_RETURN) or the note is revenue-only (price revision, discount, etc.).
    if ((voucherType == VocuherTypes.CREDIT_NOTE || voucherType == VocuherTypes.DEBIT_NOTE) && !invoice.reason) {
      toast.error("Please select a reason");
      return;
    }

    if (tagsMandatory && !(invoice.tags && invoice.tags.length > 0)) {
      toast.error("Please select a tag");
      return;
    }

    if (invoiceCalculation.taxType != isTaxTypeIgst) {
      console.log(`invoiceCalculation.taxType = ${invoiceCalculation.taxType} and isTaxTypeIgst = ${isTaxTypeIgst}`)
      toast.error("Oops! Incorrect Place of supply. Please check invoice tax type");
      return
    }

    // Validate additional charges have ledger mapping
    if (invoice.additionalCharges.apply && invoice.additionalCharges.charges && invoice.additionalCharges.charges.length > 0) {
      const unmappedCharges = invoice.additionalCharges.charges.filter(
        (charge: any) => !charge.ledgerId || !charge.ledgerName || !charge.value || charge.value <= 0
      );

      if (unmappedCharges.length > 0) {
        toast.error("Please ensure all additional charges have amount > 0 and selected accounting ledger");
        return;
      }
    }

    data.date = valueDate.toString();
    data.invoiceExpiryDate = invoiceExpiryDate.toString();
    data.voucherCategory = type;
    data.voucherType = voucherType;


    data.payment = { valueDate: valueDate.toString() };
    console.log("------------ placeOfSupply on submit ----------- ", placeOfSupply)
    if (placeOfSupply && placeOfSupply != '') {
      data.placeOfSupply = placeOfSupply;
    } else {
      delete data.placeOfSupply
    }
    data.terms = data.termsContitons.text != '' ? data.termsContitons.text : '';
    data.pdfTemplate = data.pdfTemplate; // "62d1544a47691bed1325f35e"; //data1.pdfTemplate;
    data.payTo = payToObj;
    // Tags live in `invoice` state, not react-hook-form, so take them from there.
    // Always send the array so clearing every tag actually persists.
    data.tags = invoice.tags || [];
    // Reason (credit/debit note) also lives in `invoice` state, not the form.
    data.reason = invoice.reason || '';

    data.lineItems = Object.values(inLineItems);
    // Drafts don't move stock, so the server skips its stock check for them.
    (data as any).isDraft = isDraft;

    data.taxableAmount = invoiceCalculation.taxableAmount;
    (data as any).flatDiscount = savedFlat + flatAmt;
    data.amount = Number(invoiceCalculation.totalAmount);
    data.totalPayableAmount = invoiceCalculation.totalPayableAmount;
    data.finalAmount = invoiceCalculation.finalAmount;

    data.tax1 = invoiceCalculation.sgstAmount.toString(); // sgst
    data.tax2 = invoiceCalculation.cgstAmount.toString(); // cgst
    data.tax3 = invoiceCalculation.igstAmount.toString(); // igst

    data.tcs = { ...data.tcs, ...invoiceCalculation.tcs, tcsSelected };
    data.tds = { ...data.tds, ...invoiceCalculation.tds, tdsSelected };

    data.appliedCgstSgstTaxRates = invoiceCalculation.appliedCgstSgstTaxRates;
    data.appliedIgstTaxRates = invoiceCalculation.appliedIgstTaxRates;
    data.isTaxTypeIgst = isTaxTypeIgst;

    // Split lines come straight from the popup when it saves, since state isn't updated yet.
    const payLines = split || splitPayments;
    const splitTotal = Number(payLines.reduce((sum, l) => sum + Number(l.amount), 0).toFixed(2));
    data.receivedAmount = payLines.length ? splitTotal : receivedAmount;
    data.paymentMode = (payLines.length ? payLines[0].paymentMode : paymentMode).toUpperCase();
    (data as any).payments = payLines.length ? payLines : undefined;

    if (type === VocuherCategory.SALE) {
      data.supplier = selectedAccount._id;
      data.buyer = selectedClient._id;
      data.invoiceAccountRelation = 'AR';
    } else {
      // else if type === VocuherCategory.PURCAHSE
      data.supplier = selectedClient._id;
      data.buyer = selectedAccount._id;
      data.invoiceAccountRelation = 'AP';
    }

    data.account = selectedAccount._id;
    data.client = selectedClient._id;

    // Invoice raised for one of the buyer's family members: keep a snapshot of who.
    const member = invoiceMember;
    (data as any).familyMember = member ? {
      memberId: member._id || member.memberId,
      name: member.name,
      relation: member.relation || '',
      gender: member.gender || '',
      age: ageOnInvoice(member),
      phone: member.phone || '',
    } : null;

    // Carry external CRM (e.g. Unofy) provenance onto the invoice when the buyer
    // was imported from a CRM. Stored as plain strings on the invoice; `source`
    // comes straight from the CRM response (empty if it didn't send one).
    if (selectedClient.crmLeadId) {
      data.crmLeadId = selectedClient.crmLeadId;
      data.source = selectedClient.crmSource || '';
    }

    // Lead section: the resolved label is what the invoice keeps as its source. The
    // three picks themselves are transport-only — the backend forwards them to the
    // CRM's patient-visit call and never saves them.
    if (crmLeadEnabled) {
      if (crmLead.sourceValue) data.source = crmLead.sourceValue;
      if (crmLead.agentName) (data as any).crmAgentName = crmLead.agentName;
      (data as any).crmLead = {
        sourceName: crmLead.sourceLabel || '',
        subSourceName: crmLead.subSourceLabel || '',
        agentId: crmLead.agentId || '',
      };
    }

    let address = selectedClient.addresses;
    let shippingAddress = address.find((item: any) => item.type === 'Shipping');
    let billingAddress = address.find((item: any) => item.type === 'Billing');
    data.billingAddress = (billingAddress) ? billingAddress._id : selectedClient._id;
    data.shippingAddress = (shippingAddress) ? shippingAddress._id : selectedClient._id;

    if (searchParamRefMultipleInvoiceIds && searchParamRefMultipleInvoiceIds.split(",").length > 0) {
      data.refInvoiceIds = (searchParamRefMultipleInvoiceIds && searchParamRefMultipleInvoiceIds.split(",").length > 0) ? searchParamRefMultipleInvoiceIds.split(",") : [];
    }

    if (selectedAgent) data.agent = selectedAgent._id;
    const docsIds = docData.map(({ _id }) => _id);
    data.attachments = docsIds;

    if ((voucherType == VocuherTypes.CREDIT_NOTE) || (voucherType == VocuherTypes.DEBIT_NOTE)) {
      data.quotationDate = '';
      data.quotationNumber = ''
      data.poDate = '';
      data.poNumber = ''
    } else {
      // Migrated invoices carry no quotation/PO date; new Date(undefined) would throw on toISOString.
      const toIso = (d: any) => (d && !isNaN(new Date(d).getTime()) ? new Date(d).toISOString() : '');
      data.quotationDate = toIso(quotationDate);
      data.quotationNumber = data.quotationNumber;
      data.poDate = toIso(poDate);
      data.poNumber = data.poNumber;
    }
    data.payBydate = payByDate.toString();
    data.balanceAmount = payLines.length ? Number((Number(invoiceCalculation.finalAmount) - splitTotal).toFixed(2)) : balanceAmount;

    if (selectedLocationId) {
      data.locationId = selectedLocationId;
    }

    console.log("Final Data Invoice =========== ", data);

    try {
      if (!isEditing) {
        await dispatch(addInvoice(data)).unwrap().then(async (responseData) => {
          if (responseData) {
            let _invoiceId = responseData.invoice._id;
            console.log("invoice created successfully. setting up newInvoiceID ", responseData.invoice._id)
            setIsDirty(false);

            // If not a draft save and no payment received, update status from DRAFT to PENDING
            // If payment received, let backend's status (PAID/PARTIALLY_PAID) remain
            if (!isDraft && _invoiceId && data.receivedAmount === 0) {
              console.log("Updating invoice status to PENDING", _invoiceId);
              try {
                const statusUpdateResult = await dispatch(updateInvoiceStatus({
                  id: _invoiceId,
                  invoiceStatus: 'PENDING'
                })).unwrap();
                console.log("Invoice status updated successfully:", statusUpdateResult);
                toast.success(`${pageTitle} created successfully with PENDING status`);
              } catch (statusError) {
                console.error("Error updating invoice status:", statusError);
                toast.error("Invoice created but failed to update status to PENDING");
              }
            } else if (!isDraft && _invoiceId && data.receivedAmount > 0) {
              // Payment received, backend already set correct status (PAID/PARTIALLY_PAID)
              console.log("Invoice created with payment, backend status preserved");
              toast.success(`${pageTitle} created successfully`);
            } else if (isDraft) {
              toast.success(`${pageTitle} created successfully with DRAFT status`);
            }

            // Save & View: reload a blank form and show the saved invoice in the drawer; drafts still go to preview.
            if (_invoiceId && !isDraft && !isDispatched) navigate(pathname + location.search, { replace: true, state: { savedInvoiceId: _invoiceId, savedTemplateId: data.pdfTemplate, share: shareAfterSaveRef.current } });
            else if (_invoiceId) setNewInvoiceId(_invoiceId);
          }
        });
      } else {
        if (invoiceId) {
          // Tell the audit trail exactly what the user changed. The payload is fully
          // recalculated by the form (taxes, totals, balances all get resent), and the
          // backend diff can't see per-line-item edits reliably — so we compute the
          // real change list here (against the originally-loaded invoice) and pass it.
          (data as any)._auditChanges = computeAuditChanges(invoiceDetails, data);

          await dispatch(updateInvoice({ id: invoiceId, invoicePayload: data })).unwrap()
          setIsDirty(false);

          // If not a draft save and invoice status was PENDING, ensure it stays PENDING
          if (!isDraft && invoiceDetails?.status === 'PENDING' && data.receivedAmount === 0) {
            console.log("Updating invoice status to PENDING after edit", invoiceId);
            try {
              await dispatch(updateInvoiceStatus({
                id: invoiceId,
                invoiceStatus: 'PENDING'
              })).unwrap();
              console.log("Invoice status preserved as PENDING");
            } catch (statusError) {
              console.error("Error preserving PENDING status:", statusError);
            }
          }

          // Update & Share: reload this page with the share drawer open, same as Save & Share.
          if (shareAfterSaveRef.current && !isDraft) navigate(pathname + location.search, { replace: true, state: { savedInvoiceId: invoiceId, savedTemplateId: data.pdfTemplate, share: true } });
          // Set last — this triggers the navigate, and the preview re-fetches the invoice.
          else setNewInvoiceId(invoiceId);
        }
      }
    } catch (error: any) {
      // Empty catch block.
    }
  };

  // usePrompt({isDirty});

  const onSplitSave = (lines: SplitPaymentLine[]) => {
    const total = Number(lines.reduce((sum, l) => sum + l.amount, 0).toFixed(2));
    setSplitPayments(lines);
    if (lines.length) setPaymentMode(lines[0].paymentMode);
    const nextInvoice = { ...invoice, fullyPaid: false, receivedAmount: total };
    setInvoice(nextInvoice);
    setReceivedAmount(total);
    setSplitDialogOpen(false);
    if (lines.length) onSubmit(nextInvoice, false, lines);
  };

  //Method to handle Cancel button click.
  const onCancel = () => {
    methods.reset();
    const cancelLink = pathname.includes('sales') ? `/sales` : `/purchases`;
    navigate(cancelLink);
  };

  const getInvoiceDetails = async () => {
    setIsLoading(true);
    // const s = { ...invoice };
    // s.clientAddress.active = false;
    // setInvoice(s);
    const timerId = setTimeout(async () => {
      await dispatch(fetchInvoiceSeqNumber(`${voucherType}`)).then(async (data: any) => {
        if (data.payload) {
          setIsFormLoaded(true);
          setInvoiceSeqData(data.payload);
        } else
          methods.trigger("invoiceNumber");
      }).catch(e => {
        setIsLoading(false);
        setIsFormLoaded(false);
        // console.log(e);
      });
    }, 200);  // 200ms delay
  };

  const docDelete = (id: any) => {
    setDocDeleteId(id)
  }



  useEffect(() => {
    if (!selectedAccount?._id) return;
    dispatch(fetchAccountSettings(selectedAccount._id)).unwrap()
      .then((res: any) => setWaInvoiceCreatedOn(!!res?.settings?.whatsappNotifications?.invoiceCreated))
      .catch(() => setWaInvoiceCreatedOn(null));
  }, [selectedAccount?._id, dispatch]);

  // Save & Share: wait for the background PDF build, then open the share drawer on it.
  useEffect(() => {
    if (!savedView.share) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: saved }: any = await axios.get(`/api/v1/invoice/${savedView.savedInvoiceId}`);
        for (let tries = 0; tries <= 20 && !cancelled; tries++) {
          const res: any = await dispatch(fetchInvoicePreview({ invoiceId: savedView.savedInvoiceId, templateId: savedView.savedTemplateId || saved?.pdfTemplate }));
          const entry = Array.isArray(res?.payload) ? res.payload[0] : res?.payload;
          if (entry?.Location && !entry.pdfStale) {
            if (!cancelled) setShareData({ preview: entry, invoice: saved });
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 3000));
        }
      } catch (e) { }
      if (!cancelled) {
        toast.error('Invoice PDF is not ready yet. Share it from Previous Invoices.');
        setShareDrawerOpen(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch current invoice details on edit
  useEffect(() => {
    if (invoiceId) {
      dispatch(fetchInvoiceById(invoiceId));
    }
  }, [invoiceId, dispatch]);

  // Clear invoice details when creating new invoice (not editing or copying)
  // Also clears when voucher type or category changes
  // Triggers on pathname change to detect navigation from edit/view to create
  useEffect(() => {
    if (!invoiceId && !searchParamRefInvoiceId && !searchParamRefMultipleInvoiceIds) {
      // This is a truly new invoice or voucher type changed, clear Redux state and all form data
      dispatch(clearInvoiceDetails());

      // Clear client/buyer data
      setSelectedClient(undefined);
      setSelectedClientBillToStateId(undefined);
      setSelectedClientShipToStateId(undefined);
      setSelectedBuyer(undefined);
      setSelectedAgent(undefined);

      // Clear line items
      setInLineItems([]);
      setEditInLineItems([]);

      // Clear reference invoice data
      setSelectedInvoiceP('');

      // Clear payment and tax data
      setPayToObj(undefined);
      setSplitPayments([]);
      setTcsSelected({
        _id: "",
        label: "",
        rate: 0
      });
      setTdsSelected({
        _id: "",
        label: "",
        rate: 0
      });

      // Clear document attachments
      setDocData([]);

      // Clear invoice sequence data to force refresh of invoice number
      setInvoiceSeqData(undefined);

      // Clear form field values
      methods.setValue("client", "");
      methods.setValue("agent", "");

      // Reset invoice state to initial values
      setInvoice((prevInvoice: any) => ({
        ...InvoiceElements,
        clientAddress: {
          active: false,
          billTo: {
            address: String(""),
            state: String(""),
            pincode: 0,
            city: String("")
          },
          shipTo: {
            address: String(""),
            state: String(""),
            pincode: 0,
            city: String("")
          }
        }
      }));
    }
  }, [invoiceId, searchParamRefInvoiceId, searchParamRefMultipleInvoiceIds, vType, type, pathname, dispatch, methods]);


  useEffect(() => {
    if (docDeleteId) {
      let docs = docData.filter(doc => doc._id !== docDeleteId);
      setDocData([...docs]);
      setDocDeleteId('');
    }
  }, [docDeleteId])

  useEffect(() => {
    const navigateDispatch = async () => {
      console.log("navigateDispatch called ", newInvoiceId, "isDispatched:", isDispatched, "isEditing:", isEditing)
      if (newInvoiceId) {
        if (isDispatched && !isEditing) {
          // Only dispatch for NEW invoices, not updates
          console.log("Dispatching new invoice")
          let currentData = methods.getValues();
          await dispatch(dispatchInvoice({ invoiceId: newInvoiceId, params: queryParams, dispatchPayload: { templateId: currentData.pdfTemplate } })).then(async (data: any) => {
            if (data.payload) {
              setIsDispatched(false); // Reset after dispatch
              onCancel();
            }
          });
        } else {
          // Navigate to preview for both new and updated invoices
          console.log("navigateDispatch called in else  ", newInvoiceId)
          navigate(`/invoice/${newInvoiceId}/preview?templateId=${invoice.pdfTemplate}&type=${invoice.voucherCategory}`);
        }
      }
    }
    // Commenting navigateDispatch() call to avoid send Email to buyer mail address. Invoice will be shared with share invoice action menu
    navigateDispatch();
  }, [isDispatched, newInvoiceId, dispatch, methods, isEditing])

  // Set edit invoice data in case of edit.
  useEffect(() => {
    const setInitialData = async () => {

      console.log("called setInitialData from main ");
      console.log(invoiceDetails);
      console.log(isEditing);
      try {

        setInvoice(InvoiceElements)
        // const _s = {...invoice};
        // const newInvoice = JSON.parse(JSON.stringify(_s)) as typeof _s;

        const newInvoice = { ...invoice };
        console.log(`DDD--- ${searchParamVoucherType}  ${VocuherTypes.ESTIMATE} && ${searchParamCopyFlag}`)
        if (invoiceDetails || selectedInvoiceP) {
          console.log("called setInitialData from isEditing ", isEditing);
          setIsFormLoaded(true);
          setIsClientLoading(true);
          setIsLoading(true);
          setIsBankChangeLoading(true);

          if (selectedInvoiceP && selectedInvoiceP != '') {
            console.log("==== Cond1: Matched selectedInvoiceP && selectedInvoiceP not empty ===== ", selectedInvoiceP)
            if (searchParamCopyFlag && searchParamRefInvoiceId) {
              console.log("==== Cond1: refInvoiceNumber, refInvoiceId ", searchParamCopyFlag, searchParamRefInvoiceId)
              newInvoice.refInvoiceNumber = selectedInvoiceP.invoiceNumber || '';
              newInvoice.refInvoiceId = selectedInvoiceP._id;
            }
            if (voucherType == VocuherTypes.CREDIT_NOTE || voucherType == VocuherTypes.DEBIT_NOTE) {
              newInvoice.refInvoiceNumber = selectedInvoiceP.invoiceNumber || '';
              newInvoice.refInvoiceId = selectedInvoiceP._id;
              newInvoice.poNumber = selectedInvoiceP.poNumber;
              newInvoice.poDate = selectedInvoiceP.poDate;
              newInvoice.quotationNumber = selectedInvoiceP.quotationNumber;
              newInvoice.quotationDate = selectedInvoiceP.quotationDate;
              setPoDate(selectedInvoiceP.poDate);
              setQuotationDate(selectedInvoiceP.quotationDate);
            } else if (searchParamVoucherType == VocuherTypes.ESTIMATE && String(searchParamCopyFlag) === String(false)) {
              console.log("Setting up quotation....")
              newInvoice.quotationNumber = selectedInvoiceP.invoiceNumber;
              newInvoice.quotationDate = selectedInvoiceP.date;
              setQuotationDate(selectedInvoiceP.date);
            }

            if (selectedInvoiceP.placeOfSupply && selectedInvoiceP.placeOfSupply != "") {
              newInvoice.placeOfSupply = selectedInvoiceP.placeOfSupply;
              var stateValue = findState(selectedInvoiceP.placeOfSupply);
              asigndata(stateValue)
              setPlaceOfSupply(selectedInvoiceP.placeOfSupply);
            }
            // setPayByDate(selectedInvoiceP.payBydate);
            newInvoice.additionalCharges = selectedInvoiceP.additionalCharges || newInvoice.additionalCharges;
            newInvoice.additionalFields = selectedInvoiceP.additionalFields || newInvoice.additionalFields;

            newInvoice.tcs = selectedInvoiceP.tcs || newInvoice.tcs;
            newInvoice.tds = selectedInvoiceP.tds || newInvoice.tds;
            newInvoice.receivedAmount = selectedInvoiceP.receivedAmount;
            newInvoice.balanceAmount = (searchParamCopyFlag && searchParamRefInvoiceId) ? 0 : selectedInvoiceP.balanceAmount;
            if (selectedInvoiceP.tcs?.tcsSelected) setTcsSelected(selectedInvoiceP.tcs.tcsSelected);
            if (selectedInvoiceP.tds?.tdsSelected) setTdsSelected(selectedInvoiceP.tds.tdsSelected);

            newInvoice.account = selectedAccount.name;
            newInvoice.terms = selectedInvoiceP.terms;
            newInvoice.termsContitons = selectedInvoiceP.termsContitons;
            // selectedInvoiceP is always a *reference* document (copy source, converted quotation,
            // credit/debit note origin) and never the document being edited, so its journalEntry
            // must not be carried over — it belongs to that other voucher.
            // A credit / debit note prints with its own voucher default (Qty column etc.), not the invoice's layout.
            if (!isNoteVoucher) newInvoice.pdfTemplate = selectedInvoiceP.pdfTemplate;
            // Carry the source document's tags over to the copy/conversion.
            newInvoice.tags = (selectedInvoiceP.tags || []).map((tag: any) => tag?._id || tag);

            if (refNote != '' && groupedLineItems && groupedLineItems.length > 0) {
              newInvoice.ref1 = refNote;
            }

            if (selectedInvoiceP.notes) {
              newInvoice.notes.active = selectedInvoiceP.notes.active;
              newInvoice.notes.text = selectedInvoiceP.notes.text;
            }
            if (selectedInvoiceP.emailSubject) {
              newInvoice.emailSubject.active = selectedInvoiceP.emailSubject.active;
              newInvoice.emailSubject.text = selectedInvoiceP.emailSubject.text;
            }
            if (selectedInvoiceP.emailText) {
              newInvoice.emailText.active = selectedInvoiceP.emailText.active;
              newInvoice.emailText.text = selectedInvoiceP.emailText.text;
            }

            // Counterparty id: client for SALE (credit note), supplier for PURCHASE (debit note); fall back to buyer.
            const counterpartyId = selectedInvoiceP?.client?._id || selectedInvoiceP?.supplier?._id || selectedInvoiceP?.buyer?._id;
            if (counterpartyId) {
              let selectedClient = await dispatch(fetchCounterPartyAccountById(counterpartyId));
              if (selectedClient.payload) setSelectedClient(selectedClient.payload);
            }

            console.log("From condtion 1 groupedlineitems ==> ", groupedLineItems);

            if (groupedLineItems && groupedLineItems.length > 0) {
              console.log("From condtion 1 isnide if ==> ", groupedLineItems.length);
              setInLineItems(groupedLineItems);
              setEditInLineItems(groupedLineItems);
            } else {
              setInLineItems(selectedInvoiceP.lineItems);
              setEditInLineItems(selectedInvoiceP.lineItems);
            }

            if (type === VocuherCategory.SALE) {
              newInvoice.bankDetails.active = true;
              setPayToAccount(selectedAccount);
              setPayToObj(selectedInvoiceP.payTo);
            }
          }

          if (invoiceDetails && Object.keys(invoiceDetails).length > 0) {
            console.log("==== Cond2: Matched only for few required fields invoiceDetails =====", invoiceDetails)

            // if IsEditing, else it will fetch the invoice number, date, from invoiceSeqData
            if (isEditing) {
              newInvoice.invoiceNumber = invoiceDetails.invoiceNumber;
              newInvoice.date = invoiceDetails.date;
              setValueDate(invoiceDetails.date);
              // Migrated invoices may lack due/pay-by dates; fall back so submit's toString() doesn't crash.
              const expiryDate = invoiceDetails.invoiceExpiryDate || invoiceDetails.payBydate || invoiceDetails.date;
              newInvoice.invoiceExpiryDate = expiryDate;
              setInvoiceExpiryDate(expiryDate);
              setInvoiceStatus(invoiceDetails.status)
              setPayByDate(invoiceDetails.payBydate || expiryDate);
              // Comes back populated on some responses, so reduce it to an id.
              setSelectedLocationId((invoiceDetails.locationId as any)?._id || invoiceDetails.locationId || '');
              newInvoice.paymentTerms = invoiceDetails.paymentTerms;
              newInvoice.pdfTemplate = invoiceDetails.pdfTemplate;
              newInvoice.account = selectedAccount.name;
              newInvoice.terms = invoiceDetails.terms;
              newInvoice.termsContitons = invoiceDetails.termsContitons;
              newInvoice.journalEntry = invoiceDetails.journalEntry || '';
              // `tags` comes back populated from the API, so reduce it to ids.
              newInvoice.tags = (invoiceDetails.tags || []).map((tag: any) => tag?._id || tag);
              // The invoice keeps the attribution as a plain label; re-picking the buyer
              // and its Lead section replaces it.
              setCrmLead({ sourceValue: invoiceDetails.source || '' });
              if (invoiceDetails.notes) {
                newInvoice.notes.active = invoiceDetails.notes.active;
                newInvoice.notes.text = invoiceDetails.notes.text;
              }
              if (invoiceDetails.emailSubject) {
                newInvoice.emailSubject.active = invoiceDetails.emailSubject.active;
                newInvoice.emailSubject.text = invoiceDetails.emailSubject.text;
              }
              if (invoiceDetails.emailText) {
                newInvoice.emailText.active = invoiceDetails.emailText.active;
                newInvoice.emailText.text = invoiceDetails.emailText.text;
              }

              setDocData(invoiceDetails.attachments);
            }
            // else if (searchParamRefInvoiceId && (searchParamVoucherType == VocuherTypes.ESTIMATE || searchParamVoucherType == VocuherTypes.PROFORMA_INVOICE)) {
            else if (searchParamRefInvoiceId || searchParamRefMultipleInvoiceIds) {
              console.log("else it will fetch the invoice number, date, from invoiceSeqData");
              getInvoiceDetails();
              setDocData(invoiceDetails.attachments)
            }
            // newInvoice.account = selectedAccount.name;
            // newInvoice.terms = invoiceDetails.terms;
            // newInvoice.termsContitons = invoiceDetails.termsContitons;
            // if(invoiceDetails.notes) {
            //   newInvoice.notes.active = invoiceDetails.notes.active;
            //   newInvoice.notes.text = invoiceDetails.notes.text;
            // }

            // newInvoice.footer1 = invoiceDetails.footer1;
          }

          if (invoiceDetails && selectedInvoiceP == undefined || selectedInvoiceP == '') {
            console.log("==== Cond3: Matched only invoiceDetails && selectedInvoiceP is empty =====", invoiceDetails.payBydate)
            newInvoice.refInvoiceNumber = invoiceDetails.refInvoiceNumber;
            newInvoice.refInvoiceId = invoiceDetails.refInvoiceId || "";
            setPayByDate(invoiceDetails.payBydate);
            newInvoice.paymentTerms = invoiceDetails.paymentTerms;
            newInvoice.poNumber = invoiceDetails.poNumber;
            newInvoice.poDate = invoiceDetails.poDate;
            newInvoice.quotationNumber = invoiceDetails.quotationNumber;
            newInvoice.quotationDate = invoiceDetails.quotationDate;
            setPoDate(invoiceDetails.poDate);
            setQuotationDate(invoiceDetails.quotationDate);
            newInvoice.additionalCharges = invoiceDetails.additionalCharges || newInvoice.additionalCharges;
            newInvoice.additionalFields = invoiceDetails.additionalFields || newInvoice.additionalFields;
            if (isEditing) {
              const flat = Number(invoiceDetails.flatDiscount) || 0;
              const lines: any[] = ((groupedLineItems && groupedLineItems.length ? groupedLineItems : invoiceDetails.lineItems) || []).filter(Boolean);
              // Lines saved with their pre-discount values can show the discount again; older ones just carry the amount.
              if (flat > 0 && lines.some((li: any) => li._preFlat)) {
                setFlatOn(true);
                setFlatDiscount(String(flat));
              } else {
                // Carry only what the lines still hold below MRP, so a stale amount isn't saved again.
                const gap = lines.every((li: any) => Number(li.mrp) > 0) ? lines.reduce((n: number, li: any) => n + Math.max(0, Number(li.mrp) * Number(li.qty) - Number(li.total)), 0) : flat;
                setSavedFlat(Math.max(0, Math.min(flat, Number(gap.toFixed(2)))));
              }
            }
            // invoiceDetails holds the edited document when editing, but the *source* document
            // when copying (both are loaded by fetchInvoiceById into the same slot). Only an edit
            // may keep the journalEntry; a copy would otherwise inherit the source's entry.
            if (isEditing) {
              newInvoice.journalEntry = invoiceDetails.journalEntry || '';
            }

            if (invoiceDetails.refInvoiceIds && invoiceDetails.refInvoiceIds.length > 0) { newInvoice.refInvoiceIds = invoiceDetails.refInvoiceIds; }
            if (invoiceDetails.ref1 && invoiceDetails.refInvoiceIds.ref1 != '') { newInvoice.ref1 = invoiceDetails.ref1; }

            newInvoice.tcs = invoiceDetails.tcs || newInvoice.tcs;
            newInvoice.tds = invoiceDetails.tds || newInvoice.tds;
            // newInvoice.receivedAmount = invoiceDetails.receivedAmount;
            newInvoice.balanceAmount = invoiceDetails.balanceAmount;
            if (invoiceDetails.tcs?.tcsSelected) setTcsSelected(invoiceDetails.tcs.tcsSelected);
            if (invoiceDetails.tds?.tdsSelected) setTdsSelected(invoiceDetails.tds.tdsSelected);

            if (invoiceDetails.placeOfSupply && invoiceDetails.placeOfSupply != "") {
              newInvoice.placeOfSupply = invoiceDetails.placeOfSupply;
              const _stateValue = findState(invoiceDetails.placeOfSupply);
              asigndata(_stateValue)
              setPlaceOfSupply(invoiceDetails.placeOfSupply);
            }

            let selectedClient = await dispatch(fetchCounterPartyAccountById(invoiceDetails?.client?._id));
            if (type === VocuherCategory.SALE) {
              newInvoice.bankDetails.active = true;
              setPayToAccount(selectedAccount)
              setPayToObj(invoiceDetails.payTo)
            }

            setDocData(invoiceDetails.attachments)

            setSelectedClient(selectedClient.payload);
            setInvoiceMember(invoiceDetails?.familyMember?.name ? invoiceDetails.familyMember : null);
            console.log("From condtion 3 groupedlineitems ==> ", groupedLineItems);
            if (groupedLineItems && groupedLineItems.length > 0) {
              console.log("in if groupedlineitems");
              setInLineItems(groupedLineItems);
              setEditInLineItems(groupedLineItems);
            } else {
              // Edit: each saved line gets its stock cap (location stock + what this invoice already took).
              let lines = invoiceDetails.lineItems;
              if (isEditing && invoiceId) {
                try {
                  const { data: lineStock } = await axios.get(`/api/v1/invoice/${invoiceId}/line-stock`);
                  lines = (lines || []).map((li: any) => (lineStock && lineStock[li._id]) ? { ...li, ...lineStock[li._id] } : li);
                } catch (e) { /* no cap; the server still checks on save */ }
              }
              setInLineItems(lines);
              setEditInLineItems(lines);
            }
          }
          // setInvoiceStatus(selectedInvoiceData.status)
          // setIsLoading(false);
          // if (estimateDetails.status !== 'DRAFT') onCancel();

          console.log("End of initial data form setup ")
        }
        // Keep the buyer address the client effect may have already set.
        // A new note's template comes from the seq defaults, which may land before this; keep it.
        setInvoice((prev: any) => ({ ...newInvoice, clientAddress: prev.clientAddress, ...(isNoteVoucher && !isEditing && prev.pdfTemplate ? { pdfTemplate: prev.pdfTemplate } : {}) }));

        setTimeout(() => {
          setIsBankChangeLoading(false);
          setIsFormLoaded(false);
          setIsClientLoading(false);
          setIsLoading(false);
        }, 1000);
      } catch (error: any) {
        // console.log("setInitialData catch block called");
      }
    };

    // Only set initial data if we're editing, copying, or have reference invoices.
    // For credit/debit notes only, picking a reference invoice is itself the trigger.
    // (Guard is scoped to CN/DN so other voucher flows are unaffected.)
    const isCreditOrDebitNote = voucherType == VocuherTypes.CREDIT_NOTE || voucherType == VocuherTypes.DEBIT_NOTE;
    const shouldLoadData = isEditing ||
      searchParamRefInvoiceId ||
      searchParamRefMultipleInvoiceIds ||
      searchParamCopyFlag ||
      (isCreditOrDebitNote && selectedInvoiceP && selectedInvoiceP._id);

    if (shouldLoadData) {
      if (Object.keys(invoiceDetails).length > 0) setInitialData();
      if (selectedInvoiceP && Object.keys(selectedInvoiceP).length > 0) {
        console.log("selectedInvoiceP useeffect called")
        setInitialData();
      }
    }

  }, [methods, selectedInvoiceP, invoiceDetails, isEditing, selectedAccount, searchParamRefInvoiceId, searchParamRefMultipleInvoiceIds, searchParamCopyFlag]);



  useEffect(() => {
    if (invoiceSeqData) {
      if (!isEditing) {
        setIsDirty(true);
        setIsLoading(true);
        const _paymentTermDays = invoiceSeqData.paymentTermDays || 0;
        if (searchParamRefInvoiceId && !isEditing) {
          setPayByDate(new Date(Date.now() + _paymentTermDays * 24 * 60 * 60 * 1000));
          setInvoiceExpiryDate(new Date(Date.now() + _paymentTermDays * 24 * 60 * 60 * 1000));
          setInvoice({
            ...invoice,
            invoiceNumber: invoiceSeqData.invoiceNumber,
            paymentTerms: invoiceSeqData.paymentInfo,
            // Notes keep the reference's terms but use their own template.
            ...(isNoteVoucher && invoiceSeqData.defaultPdfTemplate ? { pdfTemplate: invoiceSeqData.defaultPdfTemplate } : {}),
          })
        } else {
          setPayByDate(new Date(Date.now() + _paymentTermDays * 24 * 60 * 60 * 1000));
          setInvoiceExpiryDate(new Date(Date.now() + _paymentTermDays * 24 * 60 * 60 * 1000));
          setInvoice({
            ...invoice,
            invoiceNumber: invoiceSeqData.invoiceNumber,
            termsContitons: { active: true, text: invoiceSeqData.termsAndConditions },
            paymentTerms: invoiceSeqData.paymentInfo,
            notes: { active: true, text: invoiceSeqData.notes },
            emailSubject: { active: true, text: invoiceSeqData.emailSubject },
            emailText: { active: true, text: invoiceSeqData.emailText },
            pdfTemplate: invoiceSeqData.defaultPdfTemplate,
            additionalFields: buildAdditionalFieldsFromDefaults(invoiceSeqData.additionalFieldDefaults),
          })
        }

        // methods.setValue("invoiceNumber", invoiceSeqData.invoiceNumber);
        // methods.setValue("terms", invoiceSeqData.termsAndConditions);
        // methods.setValue("footer1", invoiceSeqData.footer1);
        setIsDirty(true);

      }
      setTimeout(() => { setIsFormLoaded(false); setIsLoading(false) }, 1000);
    }
  }, [invoiceSeqData, type])

  useEffect(() => {
    if (!isEditing) setTagsMandatory(!!invoiceSeqData?.tagsMandatory);
  }, [invoiceSeqData, isEditing])

  // Edit mode skips the seq fetch, so read the voucher's tag rule on its own.
  useEffect(() => {
    if (!isEditing || !voucherType) return;
    dispatch(fetchInvoiceSeqNumber(voucherType))
      .then((data: any) => setTagsMandatory(!!data.payload?.tagsMandatory));
  }, [isEditing, voucherType, dispatch])

  useEffect(() => {
    if (type === 'account-receivable') {
      if (documentSettings.length === 0 && (invoiceSeqData || isEditing))
        dispatch(fetchDocumentSettings(selectedAccount._id));

      if (documentSettings.length > 0) {
        let obj = documentSettings.find((o: any) => o.documentType.name.toLowerCase() === 'invoice');
        setDocumentSettingId(obj?._id || '');
      }
    }
  }, [selectedAccount, documentSettings, invoiceSeqData, dispatch, type, isEditing])

  //Fetch current selected account .
  // useEffect(() => {
  //   if (fetchData)
  //     getInvoiceDetails();
  // }, [dispatch, fetchData]);

  // Fetch last invoice seq number and invoice details.
  // Re-fetch when voucher type changes
  useEffect(() => {
    if (!isEditing)
      getInvoiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, voucherType]);

  const setAddesses = (object: any) => {
    if (!object || !object.addresses) return;

    const s = { ...invoice, clientAddress: { ...invoice.clientAddress, billTo: { ...invoice.clientAddress.billTo }, shipTo: { ...invoice.clientAddress.shipTo } } };
    s.clientAddress.active = false;
    s.clientAddress.billTo.address = String("");
    s.clientAddress.shipTo.address = String("");

    let address = object.addresses;
    let shippingAddress = address.find((item: any) => item.type === 'Shipping') || [];
    let billingAddress = address.find((item: any) => item.type === 'Billing') || [];
    if (shippingAddress) {
      let countryName = countries.find((item: any) => item._id === shippingAddress.country?._id);
      let stateName = countryStates.find((item: any) => item._id === shippingAddress.state?._id);
      console.log("shippingAddress countryName ==> ", countryName);
      console.log("shippingAddress stateName ==> ", stateName);
      setSelectedClientShipToStateId(stateName?._id);
      let _shippingAdddress = [shippingAddress.line1, shippingAddress.line2, shippingAddress.city, stateName?.name, countryName?.name, shippingAddress.code];
      s.clientAddress.shipTo.address = _shippingAdddress.filter(n => n).join(", ");
    }
    if (billingAddress) {
      let countryName = countries.find((item: any) => item._id === billingAddress.country?._id);
      let stateName = countryStates.find((item: any) => item._id === billingAddress.state?._id);
      console.log("billingAddress countryName ==> ", countryName);
      console.log("billingAddress stateName ==> ", stateName);
      setSelectedClientBillToStateId(stateName?._id);
      let _billingAddress = [billingAddress.line1, billingAddress.line2, billingAddress.city, stateName?.name, countryName?.name, billingAddress.code];
      // methods.setValue("billingAddress", _billingAddress.filter(n => n).join(", "));
      s.clientAddress.billTo.address = _billingAddress.filter(n => n).join(", ");
    }
    s.clientAddress.active = true;
    setInvoice(s);
  };

  useEffect(() => {
    // setting up place of supply based on Selected Client BillTo and GST stateOfRegistration
    if (selectedClient) {
      if (selectedClient.stateOfRegistration && selectedClient.stateOfRegistration._id) {
        const _stateValue = findState(selectedClient.stateOfRegistration._id);
        asigndata(_stateValue);
        setPlaceOfSupply(selectedClient.stateOfRegistration._id);
      } else if (selectedClientBillToStateId && selectedClientBillToStateId != '') {
        const _stateValue = findState(selectedClientBillToStateId);
        asigndata(_stateValue);
        setPlaceOfSupply(selectedClientBillToStateId);
      }
    } else if (selectedAccount && stateOptions) {
      let selectedAccountAddress = selectedAccount.addresses.find((item: any) => item.type.toLowerCase() === 'Registered'.toLowerCase()) || [];

      let _accountAddress = [selectedAccountAddress.line1, selectedAccountAddress.line2, selectedAccountAddress.city, selectedAccountAddress.state?.name, selectedAccountAddress.country?.name, selectedAccountAddress.code];
      let accountAddress = _accountAddress.filter(n => n).join(", ");
      setSelectedAccountAddress(accountAddress);

      if (selectedAccountAddress.state && selectedAccountAddress.state?._id) {
        const _temp = { label: selectedAccountAddress.state?.name, value: selectedAccountAddress.state?._id }
        asigndata(_temp);
        setSelectedAccountStateId(selectedAccountAddress.state?._id);
        setPlaceOfSupply(selectedAccountAddress.state?._id);
      }
    }
    console.log(`selectedAccountStateId = ${selectedAccountStateId} and selectedClientBillToStateId = ${selectedClientBillToStateId}`);
    if (selectedAccountStateId && selectedClientBillToStateId && selectedAccountStateId != selectedClientBillToStateId) {
      setIsTaxTypeIgst(true);
    } else {
      setIsTaxTypeIgst(false);
    }
  }, [selectedClientBillToStateId])


  useEffect(() => {
    if (selectedClient) {
      setAddesses(selectedClient)
      methods.setValue("client", selectedClient.name);
      setIsClientLoading(false);
    }

    if (selectedAgent) {
      methods.setValue("agent", selectedAgent.name);
      setIsAgentLoading(false);
    }

  }, [selectedClient, selectedAgent, methods, countries]);

  useEffect(() => {
    if (!isEditing) {
      methods.setValue("account", selectedAccount.name);
      methods.setValue("date", createDate.toString());
      methods.setValue("payBydate", payByDate.toString());
      methods.setValue("paymentTerms", "Net-30");
      if (type === VocuherCategory.SALE) {
        setPayToAccount(selectedAccount)
      }
    }
  }, [methods, selectedAccount, createDate, payByDate, isEditing, type]);

  // useEffect(() => {
  //   const updateInLineFeild = async () => {
  //     if (inLineItems.length > 0) {
  //       let initialSum = 0;
  //       let sumWithInitial = inLineItems.reduce((previousValue, currentValue) => previousValue + currentValue.total, initialSum);

  //       let taxAmount = 0;
  //       let tax1 = 0;
  //       let tax2 = 0;
  //       let tax3 = 0;
  //       inLineItems.forEach((item) => {
  //         taxAmount += item.amount;
  //         tax1 += item.sgsttotal;
  //         tax2 += item.cgsttotal;
  //         tax3 += item.igsttotal;
  //       })
  //       methods.setValue("lineItem", 'filled');
  //       methods.setValue("amount", sumWithInitial);
  //       methods.setValue("amountFormat", Utils.formatNumber(sumWithInitial, true));
  //       methods.setValue("taxAmount", Utils.formatNumber(taxAmount, true));
  //       methods.setValue("tax1", tax1.toString());
  //       methods.setValue("tax1Format", Utils.formatNumber(tax1.toString(), true));
  //       methods.setValue("tax2", tax2.toString());
  //       methods.setValue("tax2Format", Utils.formatNumber(tax2.toString(), true));
  //       methods.setValue("tax3", tax3.toString());
  //       methods.setValue("tax3Format", Utils.formatNumber(tax3.toString(), true));
  //     } else
  //       methods.setValue("lineItem", '');
  //   };

  //   updateInLineFeild();
  // }, [inLineItems, methods]);

  useEffect(() => {
    if (docs.length > 0 && fetchDocs && refreshDocs) {
      let len = docs.length;
      setDocData(d => [...d, docs[len - 1]]);
    }
  }, [docs, fetchDocs, refreshDocs])

  useEffect(() => {
    if (type === VocuherCategory.PURCHASE) {
      setIsBuyerLoading(true);
      dispatch(fetchAccountsByName(selectedAccount.name)).unwrap().then((data) => {
        if (data) {
          data.accounts = data.accounts.filter(function (item) {
            return item._id == selectedAccount._id;
          });
          setSelectedBuyer(data.accounts[0]);
          setAddesses(data.accounts[0]);
          setIsBuyerLoading(false);
        }
      });
    }
  }, [selectedAccount])

  useEffect(() => {
    if (searchParamRefInvoiceId) {
      dispatch(fetchInvoiceById(searchParamRefInvoiceId)).unwrap().then((data) => {
        if (data) {
          setSelectedInvoiceP(data);
        }
      });
    }
    if (searchParamRefMultipleInvoiceIds) {
      const _tempIds = searchParamRefMultipleInvoiceIds.split(",")
      if (_tempIds && _tempIds.length > 1 && searchParamVoucherType == VocuherTypes.DELIVERY_NOTE) {
        dispatch(fetchInvoiceById(_tempIds[0])).unwrap().then((data) => {
          if (data) {
            setSelectedInvoiceP(data);
          }
        });
        dispatch(fetchSelectedInvoicesLineItem(_tempIds)).unwrap().then((data) => {
          if (data) {
            setGroupedLineItems(data.lineItems);
            setRefNote(data.refNote);
          }
        });
      }
    }
  }, [])

  const onChangeHandlerPlaceOfSupply = (event: any, value: any) => {
    // setNewOption('');
    // setIsLoading(true);

    console.log(value);

    setPlaceOfSupply(value);
    console.log("======= Called onChangeHandler ====", placeOfSupply);
    // setIsClientEdit(false);
    // setIsLoading(false);
  }



  // const stateProps = {
  //   options: stateOptions,
  //   getOptionLabel: (options) => ''+options.name
  // }

  const getdata = (data: any) => {
    statevaluechange(data);
    setPlaceOfSupply(data.value);
  }

  const findState = (stateId: any) => {
    let stateData = stateOptions.find((item: any) => item.value == stateId);
    return stateData ? stateData : {}
    // if(stateOptions && stateOptions.length > 0) {
    //   let stateData = stateOptions.find((item: any) => item.value == stateId);
    //   console.log(stateData);
    //   return stateData ? stateData : {}
    // }
    // return {}

  }

  const asigndata = (data: any) => {
    statevaluechange(data)
    setPlaceOfSupply(data.value);
  }

  const onChangeHandler1 = (event: any, value: any) => {
    // setNewOption('');
    // setIsLoading(true);
    console.log(value);
    setSelectedInvoiceP(value);

    // Set the refInvoiceNumber immediately when invoice is selected
    if (value && value.invoiceNumber) {
      setInvoiceText(value.invoiceNumber, 'refInvoiceNumber');
    } else {
      setInvoiceText('', 'refInvoiceNumber');
    }
    // setIsClientEdit(false);
    // setIsLoading(false);
  }

  const onInputChangeHandler1 = async (event: React.SyntheticEvent, value: string, reason?: string) => {
    // only re-search on real typing, so selecting an option doesn't wipe selectedInvoiceP
    if (reason && reason !== 'input') return;
    setSelectedInvoiceP('');
    // search by invoice number only (strip the " (client name)" suffix shown in the option)
    const searchName = (value || '').split(' (')[0].trim();
    const data = { name: searchName, voucherType: voucherType }
    await dispatch(fetchInvoicesByInvNumber(data)).unwrap().then((data) => {
      if (data) {
        // Draft invoices aren't issued, so a note can't be raised against them.
        const d = data.invoices.filter(function (item) {
          return item._id !== selectedAccount._id && item.status !== 'DRAFT';
        });
        setInvoiceOptions(d);
      }
    });
  };

  const handleRenderOption1 = (e: any, b: any, c: any) => {
    // console.log(c);
    // console.log(e);
    if (b && b.client != null) {
      return <li {...e} style={{ fontSize: 14, height: "auto", color: 'black' }}>{`${b.invoiceNumber} - ${b.client.name}`}</li>
    }



    // // console.log('b.counterPartyOfAccount: ', b.counterPartyOfAccount, b.name);
    // return <div {...e} className='auto-item' key={Math.random()}>
    //   <div className='img-wrapper'>
    //     <Avatar
    //       alt={b.name}
    //       imgProps={{ referrerPolicy: "no-referrer" }}
    //       src={b.logo}
    //       variant='square'
    //       style={{
    //         width: "30px",
    //         height: "30px",
    //         objectFit: "contain",
    //       }}
    //       sizes={"xs"}
    //     >{b.buyer.name.toString().substr(0, 1).toUpperCase()}</Avatar>
    //   </div>
    //   <div><b style={{ 'marginRight': '4px' }}>
    //     {b.buyer.name} ({b.invoiceNumber})
    //   </b>
    //     {/* {!b.buyer.counterPartyOfAccount && <Tooltip title="Verified account"><InfoIcon style={{ fontSize: '12px', color: '#09b2ee' }}>dagdas</InfoIcon></Tooltip>}
    //      {b.buyer.counterPartyOfAccount && <Tooltip title="My customer / vendor"><InfoIcon style={{ fontSize: '12px', color: '#aaa' }}></InfoIcon></Tooltip>} */}
    //   </div>
    // </div>
  };

  const ButtonConatiner = () => {
    return (
      <Box sx={{ border: '3px solid #fff', backgroundColor: '#fff' }}>
        <Grid container>
          <Grid item xs={12} sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
            <div style={{ order: 2, marginLeft: 'auto', display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 8 }}>
              {type === VocuherCategory.PURCHASE && <LoadingButton
                className={'buttonContainedRoot'}
                size='small'
                type="button"
                name={'scanInvoice'}
                title={ocrBlocked || ''}
                disabled={!!ocrBlocked}
                onClick={() => navigate('/purchase/scan-invoice')}
                loading={status === ApiStatus.LOADING}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, marginRight: 10 }}
                variant="contained"
              >
                <DocumentScannerIcon style={{ fontSize: 18, marginRight: 5 }} />
                Scan Invoice
              </LoadingButton>}

              {!isEditing && <LoadingButton
                className={'buttonContainedRoot'}
                size='small'
                type="button"
                name={'previousInvoices'}
                onClick={() => setPreviewDrawerOpen(true)}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, marginRight: 10 }}
                variant="contained"
              >
                Previous Invoices
              </LoadingButton>}
              {/* Save as Draft button - only visible when not editing and receivedAmount is 0 */}
              {!isEditing && receivedAmount === 0 && <LoadingButton
                className={'buttonContainedRoot'}
                size='small'
                type="button"
                name={'saveDraft'}
                onClick={(e) => onSubmit(invoice, true)}
                loading={status === ApiStatus.LOADING}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, marginRight: 10 }}
                variant="contained"
              >
                Save as Draft
              </LoadingButton>}

              {/* Main Save/Update button */}
              {<LoadingButton
                className={'buttonContainedRoot'}
                size='small'
                type="button"
                name={'submit'}
                onClick={(e) => { shareAfterSaveRef.current = false; onSubmit(invoice, false); }}
                loading={status === ApiStatus.LOADING}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, marginRight: 10 }}
                variant="contained"
              >
                {isEditing ? `Update ${pageTitle}` : 'Save & View'}
              </LoadingButton>}

              {canSaveShare && <LoadingButton
                className={'buttonContainedRoot'}
                size='small'
                type="button"
                name={'saveShare'}
                onClick={() => { shareAfterSaveRef.current = true; onSubmit(invoice, false); }}
                loading={status === ApiStatus.LOADING}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, marginRight: 10 }}
                variant="contained"
              >
                Save & Share
              </LoadingButton>}

              {canUpdateShare && <LoadingButton
                className={'buttonContainedRoot'}
                size='small'
                type="button"
                name={'updateShare'}
                onClick={() => { shareAfterSaveRef.current = true; onSubmit(invoice, false); }}
                loading={status === ApiStatus.LOADING}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, marginRight: 10 }}
                variant="contained"
              >
                Update & Share
              </LoadingButton>}

              {<LoadingButton
                className={'buttonContainedRoot'}
                size='small'
                type="button"
                name={'Cancel'}
                onClick={() => {
                  const s = { ...invoice };
                  s.clientAddress.active = false;
                  setInvoice(s);
                  methods.reset();
                  onCancel();
                }}
                loading={status === ApiStatus.LOADING}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}
                variant="contained"
              >
                {`Cancel`}
              </LoadingButton>}
              {isEditing && <LoadingButton
                className={'buttonContainedRoot'}
                size='small'
                type="button"
                name={'previousInvoices'}
                onClick={() => setPreviewDrawerOpen(true)}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, marginLeft: 10 }}
                variant="contained"
              >
                View Saved
              </LoadingButton>}
              {/* isEditing && <LoadingButton
                className={'buttonContainedRoot'}
                size='small'
                type="button"
                name={'Preview'}
                onClick={() => {
                  if (invoiceId) {
                    const previewLink = `/invoice/${invoiceId}/view?templateId=${invoice.pdfTemplate}`;
                    window.open(previewLink, '_blank');
                  }
                }}
                loading={status === ApiStatus.LOADING}
                style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7, marginLeft: 10 }}
                variant="contained"
              >
                <IconButton
                  style={{ float: 'left', color: '#545454', backgroundColor: '#f5f5f5', border: 'none', height: 25, width: 25, marginTop: 0, marginRight: 5, marginLeft: 5 }}
                  size='small'>
                  <PreviewOutlinedIcon color="info" style={{ fontSize: 20 }} />
                </IconButton>
                {`Preview`}
              </LoadingButton> */}

            </div>
            <div style={{ order: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 8, marginRight: 16 }}>
            <IconButton
              // onClick={() => {
              //   const s = { ...invoice };
              //   s.clientAddress.active = false;
              //   setInvoice(s);
              //   methods.reset();
              //   onCancel();
              // }}
              style={{ color: '#545454', backgroundColor: '#f5f5f5', border: 'none', height: 35, width: 35, marginRight: 10 }}
              size='small'
            >
              <DescriptionOutlinedIcon style={{ fontSize: 20 }} />
            </IconButton>
            <h3 style={{ margin: 0, fontWeight: 600, whiteSpace: 'nowrap' }}>{isEditing ? `Edit ${pageTitle} ( ${invoice.invoiceNumber} )` : `New ${pageTitle}`}</h3>

            {/* Hidden only when the account has no locations set up at all. */}
            {locationOptions.length > 0 &&
              <Autocomplete
                id="invoiceLocationAutocomplete"
                options={locationOptions}
                value={locationOptions.find((o: any) => o.value === selectedLocationId) || null}
                getOptionLabel={(option: any) => option.label}
                isOptionEqualToValue={(option: any, value: any) => option.value === value?.value}
                onChange={(event, value: any) => setSelectedLocationId(value ? value.value : '')}
                renderOption={(props, option: any) => (
                  <li {...props} style={{ fontSize: 14, height: 40 }}>{option.label}</li>
                )}
                style={{ width: 220, marginLeft: 20 }}
                renderInput={(params) => <TextField {...params}
                  placeholder="Select Location"
                  required
                  className='c-text-field'
                  variant="standard"
                  size='small'
                  InputProps={{ ...params.InputProps, disableUnderline: true }}
                />}
              />
            }
            </div>
          </Grid>
        </Grid>
      </Box>
    )
  };



  return (
    <>
      {/* {JSON.stringify(selectedAccount)} */}
      <BuyerModel setVisible={setBuyerModel} visible={buyerModel} />
      {(!isBankChangeLoading) ? <BankChanges onChangePayTo={payToOnChangeHandler} account={payToAccount} payToObj={payToObj} props={{ setVisible: setBankModel, visible: bankModel }} /> : ''}
      {/* {(type === 'account-receivable') && <Tooltip title="Invoice Setup">
                <SettingsIcon style={{ cursor: 'pointer', color: '#591ebb', backgroundColor: '#babbff7d', width:'30px', height:'30px', padding:'5px', borderRadius: '30px', marginLeft: '10px'}} onClick={() => { openCloseDrawer('document') }} fontSize="small" />
                </Tooltip>} */}
      {/* {JSON.stringify(invoice)} */}

      {/* <div style={{ height: 80, backgroundColor: '#fafafb', position: 'sticky', top: 0, zIndex: 990, marginLeft: -5, marginRight: -5 }} /> */}
      {(isLoading || (status === ApiStatus.LOADING)) ? (
        <>
          <Grid container p={2} >
            <Grid xs={12} style={{ textAlign: 'center' }}>
              <CircularProgress color="success" />
              <Typography>Please Wait ...</Typography>
            </Grid>
          </Grid>
        </>
      ) : (
        <>

          <Card variant="outlined" style={{ height: 'fit-content' }} >
            {isLoading && <StyledBoxLoader component="div" sx={{ mt: 1 }}><CircularProgress color="inherit" /></StyledBoxLoader>}

            <Box sx={{ flexGrow: 1, p: 0 }} component={'form'}>
              <Grid
                container
                spacing={1}
                sx={{
                  '--Grid-borderWidth': '1px',
                  borderTop: 'var(--Grid-borderWidth) dashed',
                  borderLeft: 'var(--Grid-borderWidth) dashed',
                  borderColor: 'divider',
                  '& > div': {
                    borderRight: 'var(--Grid-borderWidth) dashed',
                    borderBottom: 'var(--Grid-borderWidth) dashed',
                    borderColor: 'divider',
                  },
                }}
              >
                {/* {isformLoaded && <StyledBoxLoader component="div" sx={{ mt: 1 }}><CircularProgress color="inherit" /> <p>Please wait...</p></StyledBoxLoader>} */}
                {/* {[...Array(6)].map((_, index) => ( */}

                <Grid item xs={12} p={0} m={0}>
                  {/* Called, not mounted — mounting remounts the location picker each render. */}
                  {ButtonConatiner()}
                </Grid>
                {/* Company info section (own name, phone, email, address, logo) hidden on the form.
                <Grid {...{ xs: 12, sm: 8, md: 4, lg: 2.5, p: 2, pl: 2 }}>
                  <>
                    <Grid container spacing={0} >
                      {isLoading ? <Skeleton variant="rectangular" width={210} height={60} /> :
                        <Grid item xs={12} p={1}>
                          <Avatar
                            alt={selectedAccount.name || 'Picture'}
                            imgProps={{
                              "referrerPolicy": 'no-referrer',
                              sx: {
                                objectFit: "cover"
                              }
                            }}
                            src={selectedAccount.logo}
                            sx={{ width: '60px !important', height: '60px !important', m: -1, mt: 0, float: 'right' }}
                            variant='rounded'
                          />
                          <h4 style={{ paddingLeft: '2px', fontWeight: 500, fontSize: 16 }} id='account'>{selectedAccount.name}</h4>
                          <Tooltip arrow title={selectedAccountAddress}>
                            <p className='c-label' >{selectedAccount.whatsAppNumber}<br />{selectedAccount.email}<br />
                              {Utils.getSubstringWithDots(selectedAccountAddress, 62)}
                            </p>
                          </Tooltip>
                        </Grid>
                      }
                    </Grid>
                  </>
                </Grid>
                */}

                {/* lg widths re-split (4.6 / 3.7 / 3.7) to use the space of the hidden company info column. */}
                <Grid {...{ xs: 12, sm: 6, md: 4, lg: 4.6, p: 2 }}>
                  {invoice.clientAddress.active === false ?
                    <Grid container spacing={1}>

                      <Grid item xs={7}>

                      </Grid>

                      <Grid item xs={5}>
                        <>
                          {type == VocuherCategory.SALE && <Button onClick={() => { openCloseDrawer('buyer') }} size='small' className='c-dashed-button' variant='outlined' sx={{ width: '100%', marginTop: '17px', borderRadius: '5px', whiteSpace: 'nowrap' }}>Select Buyer</Button>}
                          {type == VocuherCategory.PURCHASE && <Button onClick={() => { openCloseDrawer('client') }} size='small' className='c-dashed-button' variant='outlined' sx={{ width: '100%', marginTop: '17px', borderRadius: '5px', whiteSpace: 'nowrap' }}>Select Supplier</Button>}
                        </>
                      </Grid>
                      {/* <Grid item xs={2.7}>
                <Button size='small' className='c-dashed-button' variant='outlined' sx={{ width: '100%', marginTop:'17px',borderRadius:'5px'}}>Cash Sale</Button>
              </Grid> */}
                    </Grid> : <>
                      <HighlightOffTwoToneIcon onClick={() => {
                        const s = { ...invoice, clientAddress: { ...invoice.clientAddress, active: false } };
                        setInvoice(s);
                        setSelectedClient('');
                        setSelectedClientBillToStateId('');
                      }} sx={{ float: 'right', marginTop: '-2px', marginRight: -0.8, color: '#9da7b7', fontSize: 20 }} />
                      <Box sx={{ p: 1, border: '1px dashed grey', borderRadius: '10px', marginTop: '5px', minHeight: '97%' }} >
                        <Grid container spacing={1} >
                          <Grid item xs={5.9} sx={{ mt: -1.5 }}>
                            <p className='c-label' ><u>Bill To</u></p>
                            <h4 style={{ paddingLeft: '2px', marginBottom: 2, fontSize: 14 }} id='account'>{selectedClient?.name}</h4>
                            <p className='c-label' >{invoice.clientAddress.billTo.address}</p>
                          </Grid>
                          <Divider orientation='vertical' variant='fullWidth' flexItem />
                          <Grid item xs={5.9} sx={{ mt: -1.5 }}>
                            <p className='c-label' ><u>Ship To</u></p>
                            <h4 style={{ paddingLeft: '2px', marginBottom: 2, fontSize: 14 }} id='account'>{selectedClient?.name}</h4>
                            <p className='c-label' >{invoice.clientAddress.shipTo.address}</p>
                          </Grid>
                          {type == VocuherCategory.SALE && invoiceMember &&
                            <Grid item xs={12} sx={{ mt: -1 }}>
                              <p className='c-label'>Invoice For: <b style={{ color: '#1f2937' }}>{invoiceMember.name}{invoiceMember.relation ? ` (${invoiceMember.relation})` : ''}</b></p>
                            </Grid>}
                        </Grid>
                      </Box>
                    </>}
                </Grid>

                <Grid {...{ xs: 12, sm: 6, md: 4, lg: 3.7, p: 2 }}  >
                  <Grid container spacing={1}>

                    <Grid item xs={6.3}>
                      {/* ${JSON.stringify(invoiceTaxType)},  ${JSON.stringify(invoiceCalculation.taxType)} */}
                      <h5 className='c-label'> {`${voucherType} No. *`}</h5>

                      {isLoading ? <Skeleton variant="rectangular" width="100%" height={35} /> :
                        <TextField
                          fullWidth
                          style={{ width: '100%' }}
                          placeholder='Invoice Number'
                          required
                          className='c-text-field'
                          variant="standard"
                          // onChange={(e)=> setInvoiceText(e.target.value.toString(), 'invoiceNumber')}
                          value={invoice.invoiceNumber}
                          size='small'
                          InputProps={{
                            disableUnderline: true,
                          }}
                        />
                      }
                    </Grid>

                    <Grid item xs={5.7} >
                      <h5 className='c-label'>{`Date *`}</h5>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                          inputFormat="dd/MM/yyyy"
                          maxDate={moment().add(1).toDate()}
                          value={valueDate}
                          InputProps={{
                            disableUnderline: true,
                          }}
                          onChange={(value) => { handleDateChange(value, 'valueDate') }}
                          renderInput={(params) =>
                            <TextField {...params}
                              name={'valueDate'}
                              required
                              variant="standard"
                              size='small'
                              className='c-text-field'
                              placeholder="Invoice Date"
                            />}
                        />
                      </LocalizationProvider>
                    </Grid>

                    {(voucherType == VocuherTypes.CREDIT_NOTE || voucherType === VocuherTypes.DEBIT_NOTE) &&
                      <Grid item xs={12} >
                        <h5 className='c-label'>Reason</h5>
                        <Select
                          value={invoice.reason || ''}
                          onChange={(e) => setInvoiceText(e.target.value, 'reason')}
                          displayEmpty
                          className='c-text-field'
                          variant="standard"
                          size='small'
                          sx={{ width: '100%' }}
                          disableUnderline
                        >
                          <MenuItem value="" disabled>Select Reason</MenuItem>
                          {(type == VocuherCategory.PURCHASE
                            ? [{ label: 'Purchase Return', value: 'PURCHASE_RETURN' }, ...REASON_OPTIONS.filter((o) => o.value !== 'SALES_RETURN')]
                            : REASON_OPTIONS).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    }

                    {(voucherType == VocuherTypes.CREDIT_NOTE || voucherType == VocuherTypes.DEBIT_NOTE || voucherType == VocuherTypes.PURCHASE_ORDER || voucherType == VocuherTypes.ESTIMATE) ? '' :
                      <>
                        <Grid item xs={6.3} >
                          <h5 className='c-label'>Payment Term *</h5>
                          <TextField
                            fullWidth
                            style={{ width: '100%' }}
                            placeholder='Payment Term'
                            required
                            value={invoice.paymentTerms}
                            className='c-text-field'
                            onChange={(e) => setInvoiceText(e.target.value, 'paymentTerms')}
                            variant="standard"
                            size='small'
                            // onInput={(e) => {
                            //   var InputElement = (e.target as HTMLInputElement);
                            //   InputElement.value = Number(Math.max(0, parseInt(InputElement.value)).toString().slice(0, 3)) > 365 ? String('365') : String(Math.max(0, parseInt(InputElement.value)).toString().slice(0, 3));
                            // }}
                            InputProps={{
                              disableUnderline: true,
                              // endAdornment: <InputAdornment position="end"><b style={{ paddingBottom: 3, fontSize: 12 }}>Days</b></InputAdornment>,
                            }}
                          />
                        </Grid>

                        <Grid item xs={5.7} >
                          <h5 className='c-label'>Due Date *</h5>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DesktopDatePicker
                              inputFormat="dd/MM/yyyy"
                              // maxDate={moment().add(1).toDate()}
                              value={payByDate}
                              InputProps={{
                                disableUnderline: true,
                              }}
                              onChange={(value) => { handleDateChange(value, 'payBydate') }}
                              renderInput={(params) =>
                                <TextField {...params}
                                  fullWidth
                                  style={{ width: '100%' }}
                                  name={'payBydate'}
                                  required
                                  variant="standard"
                                  size='small'
                                  className='c-text-field'
                                  placeholder="Due Date"
                                />}
                            />
                          </LocalizationProvider>
                        </Grid>
                      </>
                    }
                  </Grid>
                </Grid>

                <Grid {...{ xs: 12, sm: 6, md: 4, lg: 3.7, p: 2 }} borderRight={'none !important'} >
                  {(voucherType == VocuherTypes.ESTIMATE || voucherType == VocuherTypes.PROFORMA_INVOICE || voucherType == VocuherTypes.PURCHASE_ORDER) &&
                    <Grid container spacing={1}>
                      <Grid item xs={12} >
                        <h5 className='c-label'>{`Expiry Date`}</h5>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DesktopDatePicker
                            inputFormat="dd/MM/yyyy"
                            // maxDate={moment().add(1).toDate()}
                            value={invoiceExpiryDate}
                            InputProps={{
                              disableUnderline: true,
                            }}
                            onChange={(value) => { handleDateChange(value, 'invoiceExpiryDate') }}
                            renderInput={(params) =>
                              <TextField {...params}
                                name={'invoiceExpiryDate'}
                                required
                                variant="standard"
                                size='small'
                                className='c-text-field'
                                style={{ width: '100%' }}
                                placeholder={`Expiry Date`}
                              />}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                  }
                  <Grid container spacing={1}>
                    {(voucherType == VocuherTypes.GST_INVOICE || voucherType == VocuherTypes.NON_GST_INVOICE || voucherType == VocuherTypes.PURCHASE_INVOICE || voucherType == VocuherTypes.DELIVERY_NOTE) &&
                      <>
                        <Grid item xs={6.1} >
                          <h5 className='c-label'>Quotation Date *</h5>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DesktopDatePicker
                              inputFormat="dd/MM/yyyy"
                              maxDate={moment().add(1).toDate()}
                              value={quotationDate}
                              // value={invoice.quotationDate}
                              InputProps={{
                                disableUnderline: true,
                              }}
                              onChange={(value) => { handleDateChange(value, 'quotationDate') }}
                              renderInput={(params) =>
                                <TextField {...params}
                                  fullWidth
                                  style={{ width: '100%' }}
                                  name={'quotationDate'}
                                  required
                                  variant="standard"
                                  size='small'
                                  className='c-text-field'
                                  placeholder="Quotation Date"
                                />}
                            />
                          </LocalizationProvider>
                        </Grid>

                        <Grid item xs={5.9} >
                          <h5 className='c-label'>Quotation No.</h5>
                          <TextField
                            fullWidth
                            style={{ width: '100%' }}
                            placeholder='Quotation No.'
                            value={invoice.quotationNumber}
                            onChange={(e) => setInvoiceText(e.target.value.toString(), 'quotationNumber')}
                            className='c-text-field'
                            variant="standard"
                            size='small'
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        </Grid>

                        <Grid item xs={6.1} >
                          <h5 className='c-label'>PO Date *</h5>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DesktopDatePicker
                              inputFormat="dd/MM/yyyy"
                              maxDate={moment().add(1).toDate()}
                              value={poDate}
                              InputProps={{
                                disableUnderline: true,
                              }}
                              onChange={(value) => { handleDateChange(value, 'poDate') }}
                              renderInput={(params) =>
                                <TextField {...params}
                                  fullWidth
                                  style={{ width: '100%' }}
                                  name={'poDate'}
                                  required
                                  variant="standard"
                                  size='small'
                                  className='c-text-field'
                                  placeholder="PO Date"
                                />}
                            />
                          </LocalizationProvider>
                        </Grid>

                        <Grid item xs={5.9} >
                          <h5 className='c-label'>PO No.</h5>
                          <TextField
                            fullWidth
                            style={{ width: '100%' }}
                            placeholder='PO No.'
                            value={invoice.poNumber}
                            onChange={(e) => setInvoiceText(e.target.value.toString(), 'poNumber')}
                            className='c-text-field'
                            variant="standard"
                            size='small'
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        </Grid>
                      </>
                    }
                    {(voucherType == VocuherTypes.CREDIT_NOTE || voucherType === VocuherTypes.DEBIT_NOTE) &&
                      <>
                        <Grid item xs={12}>
                          <h5 className='c-label'>Search Invoice for {voucherType}</h5>
                          <Autocomplete
                            disablePortal
                            id={`Search Ref Invoice`}
                            onChange={onChangeHandler1}
                            onInputChange={onInputChangeHandler1}
                            options={invoiceOptions}
                            renderOption={handleRenderOption1}
                            getOptionLabel={(option) => `${option.invoiceNumber} (${option.client && option.client.name})`}
                            style={{ width: '100%' }}
                            classes={{ paper: classes.paper }}
                            sx={{ width: '100%' }}
                            renderInput={(params) => <TextField {...params}
                              placeholder='Search Invoice'
                              required
                              className='c-text-field'
                              variant="standard"
                              size='small'
                              InputProps={{ ...params.InputProps, disableUnderline: true }}
                            />}
                          />
                        </Grid>
                        <Grid item xs={12} >
                          <h5 className='c-label'>Reference Invoice Number</h5>
                          <TextField
                            placeholder='Invoice Number'
                            value={invoice.refInvoiceNumber}
                            onChange={(e) => setInvoiceText(e.target.value.toString(), 'refInvoiceNumber')}
                            className='c-text-field'
                            variant="standard"
                            size='small'
                            sx={{ width: '100%' }}
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        </Grid>
                      </>
                    }
                  </Grid>

                </Grid>

                {/* ))} */}
              </Grid>
            </Box>
            <div style={{ height: 'fit-content', overflowX: 'auto', overflowY: 'auto', marginTop: 0 }}>

              {isLoading ? <div></div> :
                <TableInlineEdit3
                  onChangeLineItemRow={rowOnChangeLineItemHandler}
                  rowsData={editInLineItems}
                  isInvoiceTaxTypeIgst={isTaxTypeIgst}
                  flatDiscount={flatAmt}
                  flatLock={flatOn && Number(flatDiscount) > 0}
                  voucherType={voucherType}
                  locationId={selectedLocationId}
                />
              }
              <Box sx={{ p: 0, mt: 2 }}>
                <Grid
                  container
                  spacing={1}
                  sx={{
                    '--Grid-borderWidth': '1px',
                    borderTop: 'var(--Grid-borderWidth) dashed',
                    borderLeft: 'var(--Grid-borderWidth) dashed',
                    borderColor: 'divider',
                    '& > div': {
                      borderRight: 'var(--Grid-borderWidth) dashed',
                      // borderBottom: 'var(--Grid-borderWidth) dashed',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <Grid {...{ xs: 12, sm: 8, md: 4, lg: 3, p: 2, pl: 3 }}>

                    <Grid container spacing={0} >
                      <Grid item xs={12} mb={2}>
                        {/* <h5 className='c-label'>Place of Supply</h5> */}

                        {/* {stateOptions && 
              <CustomSelect
                name="placeOfSupply"
                label="Place of Supply"
                id="state"
                variant="outlined"
                size='small'
                options={stateOptions}
                // value={selectedState}
                value={placeOfSupply ?? ''}
                placeholder="Select Place of supply"
                // onChange={(event: SelectChangeEvent<any>) => { handleChange(event) }} 

                onChange={(event: SelectChangeEvent<any>) => {
                  methods.setValue("placeOfSupply", event.target.value);
                  setPlaceOfSupply(event.target.value);
                }}
              />
            } */}

                        {/* <Autocomplete
              id="grouped-demo"
              value={statevalue}
              options={stateOptions && stateOptions.map(e =>({ value: e.value, label: e.label }))}
              getOptionLabel={(option) => option.label}
              onChange={(event, value) => getdata(value)}
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField {...params} label="State" variant="standard"></TextField>
              )}
            />
            <br></br>
            <br></br> */}

                        <h5 className='c-label'>Place of Supply</h5>
                        <Autocomplete
                          id="stateOptionAutocomplete"
                          disablePortal
                          value={statevalue}
                          options={stateOptions && stateOptions.map(e => ({ value: e.value, label: e.label }))}
                          getOptionLabel={(option) => option.label}
                          onChange={(event, value) => getdata(value)}
                          renderOption={(props, option: { label: string }) => (
                            <li {...props}
                              style={{ fontSize: 14, height: 40 }}
                            >{option.label}</li>
                          )}
                          classes={{ paper: classes.paper }}
                          sx={{ width: '100%' }}
                          renderInput={(params) => <TextField {...params}
                            placeholder='Place of Supply'
                            required
                            className='c-text-field'
                            variant="standard"
                            size='small'
                            InputProps={{ ...params.InputProps, disableUnderline: true }}
                          />}
                        />
                      </Grid>

                      {invoice.additionalFields && invoice.additionalFields.fields.length > 0 && invoice.additionalFields.apply === true ?
                        <>
                          <Grid item xs={12}>
                            <HighlightOffTwoToneIcon onClick={() => {
                              const _s = { ...invoice };
                              const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                              s.additionalFields.apply = false;
                              s.additionalFields.fields = [];
                              setInvoice(s);
                            }}
                              sx={{ cursor: 'pointer', float: 'right', marginTop: '-2px', marginRight: -0.8, color: '#9da7b7', fontSize: 20 }}
                            />
                            <Box sx={{ p: 1.5, pt: 1, pb: 1, border: '1px dashed grey', borderRadius: '10px', marginTop: '5px', marginBottom: 2 }} >
                              {invoice.additionalFields.fields.map((record: any) =>
                                <Grid container spacing={0} mb={1}>
                                  <Grid item xs={6.3}>
                                    <TextField
                                      className='c-text-field'
                                      variant="standard"
                                      size='small'
                                      value={record.title}
                                      style={{ width: '100%' }}
                                      placeholder='Enter Field Name'
                                      onChange={(p) => {
                                        const _s = { ...invoice };
                                        const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                                        s.additionalFields.fields.map((m: any, i: number) => {
                                          if (m.id === record.id) {
                                            s.additionalFields.fields[i] = { ...m, title: p.target.value }
                                          }
                                        })
                                        setInvoice(s);
                                      }}
                                      InputProps={{
                                        disableUnderline: true
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={5} style={{ textAlign: 'end', paddingLeft: 10 }}>
                                    <TextField
                                      className='c-text-field'
                                      variant="standard"
                                      size='small'
                                      // Dropdown only when Document Settings configured it as one.
                                      select={record.fieldType === 'select'}
                                      value={record.value}
                                      style={{ width: '100%' }}
                                      onChange={(p) => {
                                        const _s = { ...invoice };
                                        const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                                        s.additionalFields.fields.map((m: any, i: number) => {
                                          if (m.id === record.id) {
                                            s.additionalFields.fields[i] = { ...m, value: p.target.value };
                                          }
                                        })
                                        setInvoice(s);
                                      }}
                                      InputProps={{
                                        disableUnderline: true,
                                      }}
                                    >
                                      {record.fieldType === 'select' && (record.options || []).map((opt: string) =>
                                        <MenuItem value={opt} key={opt}>{opt}</MenuItem>
                                      )}
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={0.7}>
                                    <HighlightOffTwoToneIcon sx={{ marginTop: '5px', marginLeft: '2px', cursor: 'pointer', color: '#9da7b7', fontSize: 20 }}
                                      onClick={() => {
                                        const _s = { ...invoice };
                                        const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                                        s.additionalFields.fields = s.additionalFields.fields.filter((a: any) => a.id !== record.id);
                                        setInvoice(s);
                                      }} />
                                  </Grid>
                                </Grid>
                              )}
                              {invoice.additionalFields.fields.length > 3 ? '' :
                                <h4 className="c-label" style={{ color: '#488dff', fontSize: 14, fontWeight: 500, marginTop: 5 }} >
                                  <a onClick={() => {
                                    if (invoice.additionalFields.fields.length > 4) { } else {
                                      const _s = { ...invoice };
                                      const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                                      s.additionalFields.fields = [...s.additionalFields.fields, {
                                        id: s.additionalFields.fields[s.additionalFields.fields.length - 1].id + 1,
                                        title: "",
                                        value: "",
                                      }];
                                      setInvoice(s);
                                    }
                                  }} style={{ cursor: 'pointer' }}><AddCircleOutlineOutlinedIcon style={{ marginBottom: -2.8, fontSize: '16px' }} /> Add Another Field</a>
                                </h4>}
                            </Box></Grid></> : <>
                          <Grid item xs={12}>
                            <h4 className="c-label" style={{ color: '#488dff', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
                              <a onClick={() => {
                                const _s = { ...invoice };
                                const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                                s.additionalFields.apply = true;
                                s.additionalFields.fields = [{
                                  id: 1,
                                  title: "",
                                  taxType: "",
                                  taxValue: 0,
                                }];
                                setInvoice(s);
                              }} style={{ cursor: 'pointer' }}><AddCircleOutlineOutlinedIcon style={{ marginBottom: -2.8, fontSize: '16px' }} /> Add Addtional Fields</a>
                            </h4>
                          </Grid>
                        </>}
                    </Grid>
                    <Grid item xs={12} mb={1} style={{ display: '' }}>
                      <TagSelect value={invoice.tags} onChange={tagsOnChangeHandler} required={tagsMandatory} suggestedTagIds={lineItemTagIds} />
                    </Grid>
                    <Grid item xs={12} mb={3} style={{ display: '' }}>
                      <TemplateSelect onChangeTemplate={pdftemplateOnChangeHandler} invoiceId={invoiceId} invoiceStatus={invoiceStatus} invoiceDocType={voucherType} defaultTemplate={invoice.pdfTemplate} />
                    </Grid>
                    {docData && <DocAttachInfo docList={docData} onDelete={docDelete} onClose={closeDoc} />}
                  </Grid>

                  <Grid {...{ xs: 12, sm: 8, md: 4, lg: 4.2, p: 3, pt: 0, pb: 0 }}>

                    <div style={{ position: 'sticky', top: 0, paddingTop: 20 }}>
                      {(!isBankChangeLoading && payToObj && invoice.bankDetails.active === true) ?
                        <>
                          <HighlightOffTwoToneIcon onClick={() => {
                            const s = { ...invoice };
                            s.bankDetails.active = false;
                            setInvoice(s);
                          }}
                            sx={{ cursor: 'pointer', float: 'right', marginTop: '-2px', marginRight: -0.8, color: '#9da7b7', fontSize: 20 }}
                          />
                          <Box sx={{ p: 1.5, pt: 1, border: '1px dashed grey', borderRadius: '10px', marginTop: '5px', marginBottom: 2 }} >
                            <Button size='small' className='c-dashed-button' variant='outlined' style={{ float: 'right', marginTop: 4 }}
                              onClick={() => setBankModel(true)}>Change</Button>
                            <h5 className='c-label' style={{ marginBottom: 5 }}><u>BANK DETAILS</u></h5>
                            <h5 className='c-label' style={{ marginBottom: 5 }}>Account Number : <label style={{ fontSize: 13, color: 'black', fontWeight: 300 }}>{payToObj.copyBankAccountDetails?.accountNumber || ''}</label></h5>
                            <h5 className='c-label' style={{ marginBottom: 5 }}>IFSC Code : <label style={{ fontSize: 13, color: 'black', fontWeight: 300 }}>{payToObj.copyBankAccountDetails?.IFSC || ''}</label></h5>
                            <h5 className='c-label' style={{ marginBottom: 5 }}>Bank & Branch Name : <label style={{ fontSize: 13, color: 'black', fontWeight: 300 }}>{payToObj.copyBankAccountDetails?.bankName || ''}</label></h5>
                            <h5 className='c-label' >Account Holder's Name : <label style={{ fontSize: 13, color: 'black', fontWeight: 300 }}>{payToObj.copyBankAccountDetails?.accountName || ''}</label></h5>
                            <h5 className='c-label' ></h5>
                          </Box>
                        </>
                        :
                        <h4 className="c-label" style={{ color: '#488dff', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
                          <a onClick={() => {
                            const _s = { ...invoice };
                            const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                            s.bankDetails.active = true;
                            setInvoice(s);
                          }} style={{ cursor: 'pointer' }}><AddCircleOutlineOutlinedIcon style={{ marginBottom: -2.8, fontSize: '16px' }} /> Add Bank Details</a>
                        </h4>
                      }

                      {invoice.termsContitons.active === true ? <>
                        <HighlightOffTwoToneIcon onClick={() => {
                          const s = { ...invoice };
                          s.termsContitons.active = false;
                          setInvoice(s);
                          setInvoiceText({ active: false, text: '' }, 'termsContitons');
                        }} sx={{ cursor: 'pointer', float: 'right', marginTop: '-7px', marginRight: -0.8, color: '#9da7b7', fontSize: 20 }} />
                        <Box sx={{ p: 1.5, pt: 1, border: '1px dashed grey', borderRadius: '10px', marginTop: '5px', marginBottom: 2 }} >
                          <h5 className='c-label' style={{ marginBottom: 5 }}>TERMS & CONDITION</h5>
                          <TextField
                            className='c-text-field'
                            variant="standard"
                            size='small'
                            multiline
                            maxRows={6}
                            minRows={2}
                            value={invoice.termsContitons.text}
                            onChange={(v) => setInvoiceText({ active: true, text: v.target.value.toString() }, 'termsContitons')}
                            style={{ width: '100%' }}
                            placeholder='Terms & Conditions'
                            InputProps={{
                              disableUnderline: true
                            }}
                          />
                        </Box></> :
                        <h4 className="c-label" style={{ color: '#488dff', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
                          <a onClick={() => {
                            const _s = { ...invoice };
                            const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                            s.termsContitons.active = true;
                            setInvoice(s);
                          }} style={{ cursor: 'pointer' }}><AddCircleOutlineOutlinedIcon style={{ marginBottom: -2.8, fontSize: '16px' }} /> Add Terms and Conditions</a></h4>}

                      {invoice.notes.active === true ? <>
                        <HighlightOffTwoToneIcon onClick={() => {
                          const s = { ...invoice };
                          s.notes.active = false;
                          setInvoice(s);
                          setInvoiceText({ active: false, text: '' }, 'notes');
                        }} sx={{ cursor: 'pointer', float: 'right', marginTop: '-7px', marginRight: -0.8, color: '#9da7b7', fontSize: 20 }} />
                        <Box sx={{ p: 1.5, pt: 1, border: '1px dashed grey', borderRadius: '10px', marginTop: '5px', marginBottom: 2 }} >
                          <h5 className='c-label' style={{ marginBottom: 5 }}>NOTES</h5>
                          <TextField
                            className='c-text-field'
                            variant="standard"
                            size='small'
                            multiline
                            maxRows={3}
                            minRows={2}
                            style={{ width: '100%' }}
                            value={invoice.notes.text}
                            onChange={(v) => setInvoiceText({ active: true, text: v.target.value.toString() }, 'notes')}
                            placeholder='Notes'
                            InputProps={{
                              disableUnderline: true
                            }}
                          />
                        </Box></> :
                        <h4 className="c-label" style={{ color: '#488dff', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
                          <a onClick={() => {
                            const _s = { ...invoice };
                            const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                            s.notes.active = true;
                            setInvoice(s);
                          }} style={{ cursor: 'pointer' }}><AddCircleOutlineOutlinedIcon style={{ marginBottom: -2.8, fontSize: '16px' }} /> Add Notes</a></h4>
                      }
                      {invoice.emailSubject.active === true ? <>
                        <HighlightOffTwoToneIcon onClick={() => {
                          const s = { ...invoice };
                          s.emailSubject.active = false;
                          setInvoice(s);
                          setInvoiceText({ active: false, text: '' }, 'emailSubject');
                        }} sx={{ cursor: 'pointer', float: 'right', marginTop: '-7px', marginRight: -0.8, color: '#9da7b7', fontSize: 20 }} />
                        <Box sx={{ p: 1.5, pt: 1, border: '1px dashed grey', borderRadius: '10px', marginTop: '5px', marginBottom: 2 }} >
                          <h5 className='c-label' style={{ marginBottom: 5 }}>SUBJECT</h5>
                          <TextField
                            className='c-text-field'
                            variant="standard"
                            size='small'
                            multiline
                            maxRows={3}
                            minRows={2}
                            style={{ width: '100%' }}
                            value={invoice.emailSubject.text}
                            onChange={(v) => setInvoiceText({ active: true, text: v.target.value.toString() }, 'emailSubject')}
                            placeholder='Subject'
                            InputProps={{
                              disableUnderline: true
                            }}
                          />
                        </Box></> :
                        <h4 className="c-label" style={{ color: '#488dff', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
                          <a onClick={() => {
                            const _s = { ...invoice };
                            const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                            s.emailSubject.active = true;
                            setInvoice(s);
                          }} style={{ cursor: 'pointer' }}><AddCircleOutlineOutlinedIcon style={{ marginBottom: -2.8, fontSize: '16px' }} /> Add Subject</a></h4>
                      }
                      {invoice.emailText.active === true ? <>
                        <HighlightOffTwoToneIcon onClick={() => {
                          const s = { ...invoice };
                          s.emailText.active = false;
                          setInvoice(s);
                          setInvoiceText({ active: false, text: '' }, 'emailText');
                        }} sx={{ cursor: 'pointer', float: 'right', marginTop: '-7px', marginRight: -0.8, color: '#9da7b7', fontSize: 20 }} />
                        <Box sx={{ p: 1.5, pt: 1, border: '1px dashed grey', borderRadius: '10px', marginTop: '5px', marginBottom: 2 }} >
                          <h5 className='c-label' style={{ marginBottom: 5 }}>EMAIL TEXT</h5>
                          <TextField
                            className='c-text-field'
                            variant="standard"
                            size='small'
                            multiline
                            maxRows={3}
                            minRows={2}
                            style={{ width: '100%' }}
                            value={invoice.emailText.text}
                            onChange={(v) => setInvoiceText({ active: true, text: v.target.value.toString() }, 'emailText')}
                            placeholder='Email Text'
                            InputProps={{
                              disableUnderline: true
                            }}
                          />
                        </Box></> :
                        <h4 className="c-label" style={{ color: '#488dff', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
                          <a onClick={() => {
                            const _s = { ...invoice };
                            const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                            s.emailText.active = true;
                            setInvoice(s);
                          }} style={{ cursor: 'pointer' }}><AddCircleOutlineOutlinedIcon style={{ marginBottom: -2.8, fontSize: '16px' }} /> Add Email Text</a></h4>
                      }
                    </div>
                  </Grid>

                  <Grid {...{ xs: 12, sm: 8, md: 4, lg: 4.8, p: 2, pl: 2 }}>
                    <Grid container spacing={0} >

                      {invoice.discount.apply === true ? <>
                        <Grid item xs={12} >
                          <HighlightOffTwoToneIcon onClick={() => {
                            const s = { ...invoice };
                            s.discount.apply = false;
                            setInvoice(s);
                          }} sx={{ cursor: 'pointer', float: 'right', marginTop: '-2px', marginRight: -0.8, color: '#9da7b7', fontSize: 20 }} />
                          <Box sx={{ p: 1.5, pt: 1, pb: 1, border: '1px dashed grey', borderRadius: '10px', marginTop: '5px', marginBottom: 2 }} >
                            <Grid container spacing={0} >
                              <Grid item xs={4.2} className='pt-3'>
                                {/* <Select
                        className='c-text-field' 
                        value={10}
                        disableUnderline
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              border: "1.5px solid #e9ecf1",
                              boxShadow: 'none',
                              borderRadius: '5px'
                            }
                          }
                          }}
                        style={{width:'100%'}}
                        size='small'
                        variant='outlined'
                        sx={{
                          boxShadow: "none",
                          textAlign:'left',
                          height: 30,
                          mt:'-1px',
                          '& .MuiOutlinedInput-input': {
                            pt: '5px',
                            pl:'5px',
                          },
                          // '& .MuiSelect-select': { p:0 },
                          
                          ".MuiOutlinedInput-notchedOutline": { border: 0 },
                          "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                            {
                              border: 0,
                            },
                          "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              border: 0,
                            },
                        }}
                      >
                        <MenuItem value={10}>Dicount Before Tax</MenuItem>
                        <MenuItem value={20}>Dicount After Tax</MenuItem>
                      </Select> */}
                                <h4 className="c-label" style={{ fontSize: 15, fontWeight: 500 }}>Dicount Before Tax</h4>
                              </Grid>
                              <Grid item xs={2.8} ></Grid>
                              <Grid item xs={2} style={{ textAlign: 'end' }}>
                                <TextField
                                  className='c-text-field'
                                  variant="standard"
                                  name='discountPercent'
                                  size='small'
                                  style={{ width: '100%', marginBottom: 3 }}
                                  value={invoice.discount.percentage}
                                  onChange={(v) => onChange(v)}
                                  InputProps={{
                                    disableUnderline: true,
                                    style: {
                                      fontSize: 12
                                    },
                                    startAdornment: <InputAdornment position="start"><b style={{ paddingBottom: 3, fontSize: 12 }}>%</b></InputAdornment>
                                  }}
                                />
                              </Grid>
                              <Grid item xs={0.5} style={{ textAlign: 'center' }}>/</Grid>
                              <Grid item xs={2.5} style={{ textAlign: 'end' }}>
                                <TextField
                                  className='c-text-field'
                                  variant="standard"
                                  name='discountAmount'
                                  value={invoice.discount.amount}
                                  size='small'
                                  style={{ width: '100%' }}
                                  onChange={(v) => onChange(v)}
                                  InputProps={{
                                    disableUnderline: true,
                                    style: {
                                      fontSize: 12
                                    },
                                    startAdornment: <InputAdornment position="start"><b style={{ paddingBottom: 3, fontSize: 12 }}>₹</b></InputAdornment>
                                  }}
                                />
                              </Grid></Grid></Box></Grid></> : <>
                        <Grid item xs={8}>
                          {/* <h4 className="c-label" style={{ color: '#488dff', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
                        <a onClick={() => {
                          const _s = { ...invoice };
                          const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                          s.discount.apply = true;
                          setInvoice(s);
                        }} style={{ cursor: 'pointer' }}><AddCircleOutlineOutlinedIcon style={{ marginBottom: -2.8, fontSize: '16px' }} /> Add Discount</a>
                      </h4> */}
                        </Grid>
                        <Grid item xs={4} style={{ textAlign: 'end' }}>
                          {/* <span>₹ 0</span> */}
                        </Grid>
                      </>}

                      {/* {JSON.stringify(invoiceCalculation)} */}
                      <Grid item xs={8}>
                        <h4 className="c-label" style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Taxable Amount</h4>
                      </Grid>
                      <Grid item xs={4} style={{ textAlign: 'end' }}>
                        <span>{Utils.formatNumber(invoiceCalculation.taxableAmount, true, true)}</span>
                      </Grid>

                      {invoiceCalculation.taxType === true ? invoiceCalculation.igstPercentage &&
                        <>
                          <Grid item xs={9}>
                            <h4 className="c-label" style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>IGST {invoiceCalculation.igstPercentage && `(${invoiceCalculation.igstPercentage})`}</h4>
                          </Grid>
                          <Grid item xs={3} style={{ textAlign: 'end' }}>
                            <span>{Utils.formatNumber(invoiceCalculation.igstAmount || 0, true, true)}</span>
                          </Grid>
                        </> :
                        <>
                          {invoiceCalculation.cgstPercentage &&
                            <>
                              <Grid item xs={9}>
                                <h4 className="c-label" style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>CGST ({invoiceCalculation.cgstPercentage})</h4>
                              </Grid>
                              <Grid item xs={3} style={{ textAlign: 'end' }}>
                                <span>{Utils.formatNumber(invoiceCalculation.cgstAmount || 0, true, true)}</span>
                              </Grid>
                            </>}
                          {invoiceCalculation.sgstPercentage && <>
                            <Grid item xs={9}>
                              <h4 className="c-label" style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>SGST ({invoiceCalculation.sgstPercentage})</h4>
                            </Grid>
                            <Grid item xs={3} style={{ textAlign: 'end' }}>
                              <span>{Utils.formatNumber(invoiceCalculation.sgstAmount || 0, true, true)}</span>
                            </Grid>
                          </>}
                        </>
                      }
                      <Grid item xs={6} mb={1.5}>
                        <ToggleButton
                          value="check"
                          size='small' sx={{ mr: 1 }}
                          className={classes.toggleButton}
                          selected={flatOn}
                          onChange={() => { setFlatOn(!flatOn); setFlatDiscount(''); }}
                        >
                          {flatOn === false ? '' : <CheckIcon style={{ fontSize: 15 }} />}
                        </ToggleButton>
                        <label onClick={() => { setFlatOn(!flatOn); setFlatDiscount(''); }} style={{ fontSize: 14, fontWeight: 500, marginBottom: 10, cursor: 'pointer' }}> Apply Flat Discount</label>
                        {/* Saved discount already inside the item prices; clearing only drops it from the printed totals. */}
                        {savedFlat > 0 && <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                          Saved flat discount {Utils.formatNumber(savedFlat, true, true)} (in item prices) <span onClick={() => setSavedFlat(0)} style={{ color: '#d32f2f', cursor: 'pointer', marginLeft: 4 }}>Remove</span>
                        </div>}
                      </Grid>
                      <Grid item xs={6} mb={1.5}>
                        {flatOn && <TextField
                          placeholder='Discount on items total'
                          type="number"
                          value={flatDiscount}
                          onChange={(e) => (!flatPct || Number(e.target.value) < 100) && setFlatDiscount(e.target.value)}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                          className='c-text-field'
                          variant="standard"
                          size='small'
                          fullWidth
                          InputProps={{
                            disableUnderline: true,
                            startAdornment: <InputAdornment position="start">
                              <Select variant="standard" disableUnderline value={flatPct ? '%' : '₹'} onChange={(e) => { setFlatPct(e.target.value === '%'); setFlatDiscount(''); }} style={{ fontSize: 14, fontWeight: 700 }}>
                                <MenuItem value="₹">₹</MenuItem>
                                <MenuItem value="%">%</MenuItem>
                              </Select>
                            </InputAdornment>,
                            endAdornment: flatPct && flatAmt > 0 ? <InputAdornment position="end">= {Utils.formatNumber(flatAmt, true, true)}</InputAdornment> : undefined,
                          }}
                        />}
                      </Grid>
                      {(voucherType == VocuherTypes.GST_INVOICE || voucherType == VocuherTypes.CREDIT_NOTE) &&
                        <Grid item xs={6} mb={1.5}>
                          <ToggleButton
                            value="check"
                            size='small' sx={{ mr: 1 }}
                            className={classes.toggleButton}
                            selected={invoice.tcs.apply}
                            onChange={() => {

                              const _s = { ...invoice };
                              const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                              s.tcs.apply = !invoice.tcs.apply;
                              if (s.tcs.apply === false) {
                                setTcsSelected({
                                  _id: "",
                                  label: "",
                                  rate: 0
                                });

                              }
                              setInvoice(s);
                            }}
                          >
                            {invoice.tcs.apply === false ? '' : <CheckIcon style={{ fontSize: 15 }} />}
                          </ToggleButton>
                          <label
                            onClick={() => {
                              const s = { ...invoice };
                              s.tcs.apply = !invoice.tcs.apply;
                              console.log(s.tcs.apply)
                              setInvoice(s);
                            }} style={{ fontSize: 14, fontWeight: 500, marginBottom: 10, cursor: 'pointer' }}> Apply TCS</label>
                        </Grid>
                      }

                      {invoice.tcs.apply === true ? <>
                        <Grid item xs={6}>
                          <Select
                            className='c-text-field'

                            disableUnderline
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  border: "1.5px solid #e9ecf1",
                                  // width: 255,
                                  // marginLeft:'-29px !important',
                                  boxShadow: 'none',
                                  borderRadius: '5px'
                                }
                              }
                            }}
                            startAdornment={
                              <InputAdornment position="start" style={{ paddingBottom: 3, fontSize: 14 }}>
                                {invoiceCalculation && invoiceCalculation.tcs ? Utils.formatNumber(invoiceCalculation.tcs.totalAmount || 0, true, true) : 0} |
                              </InputAdornment>
                            }
                            style={{ width: '100%' }}
                            value={tcsSelected._id ?? ''}
                            displayEmpty
                            onChange={onChangeTcsHandler}
                            // onChange={(e) => {
                            //   console.log(e);
                            //   setTcsSelected(tcsTaxType: e.target.value)
                            //   setCalculations({...calculations,tcsTaxType: e.target.value});
                            //   // const s = {...invoice};
                            //   // s.tcs.taxType = e.target.label;
                            //   // setInvoice(s);
                            // }}
                            size='small'
                            variant='outlined'
                            sx={{
                              boxShadow: "none",
                              // fontSize:12,
                              textAlign: 'right',
                              '& .MuiOutlinedInput-input': { p: 0.5 },
                              // '& .MuiSelect-select': { p:0 },
                              marginBottom: '3px',
                              ".MuiOutlinedInput-notchedOutline": { border: 0 },
                              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                              {
                                border: 0,
                              },
                              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                              {
                                border: 0,
                              },
                            }}
                          >
                            <MenuItem value="">None</MenuItem>
                            {tcsTaxRateOptions && tcsTaxRateOptions.map((option) =>
                              <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                            )}
                          </Select>
                        </Grid>
                        <Grid item xs={6} mb={2}></Grid>
                        <Grid item xs={3.7}>
                          <BpRadio checked={tcstype === 1} onChange={() => handleTcsClick(1)} size="small" /><span onClick={() => handleTcsClick(1)} style={{ cursor: 'pointer' }}>Total Amount</span>
                        </Grid>
                        <Grid item xs={2.3}>
                          <BpRadio checked={tcstype === 2} onChange={() => handleTcsClick(2)} size="small" /><span onClick={() => handleTcsClick(2)} style={{ cursor: 'pointer' }}>Taxable</span>
                        </Grid>
                      </>
                        : ''}

                      {(voucherType == VocuherTypes.PURCHASE_INVOICE || voucherType == VocuherTypes.DEBIT_NOTE) &&
                        <Grid item xs={6} mt={0.5} mb={1.5}>
                          <ToggleButton
                            value="check"
                            size='small' sx={{ mr: 1 }}
                            className={classes.toggleButton}
                            selected={invoice.tds.apply}
                            onChange={() => {
                              const _s = { ...invoice };
                              const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                              s.tds.apply = !invoice.tds.apply;

                              if (s.tds.apply === false) {
                                setTdsSelected({
                                  _id: "",
                                  label: "",
                                  rate: 0
                                });

                              }
                              setInvoice(s);
                            }}
                          >
                            {invoice.tds.apply === false ? '' : <CheckIcon style={{ fontSize: 15 }} />}
                          </ToggleButton>
                          <label
                            onClick={() => {
                              const s = { ...invoice };
                              s.tds.apply = !invoice.tds.apply;
                              setInvoice(s);
                            }} style={{ fontSize: 14, fontWeight: 500, marginBottom: 10, cursor: 'pointer' }}> Apply TDS</label>
                        </Grid>
                      }


                      {invoice.tds.apply === true ?
                        <>
                          <Grid item xs={6} mb={1.5}>
                            <Select
                              className='c-text-field'

                              disableUnderline
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    border: "1.5px solid #e9ecf1",
                                    // width: 255,
                                    // marginLeft:'-29px !important',
                                    boxShadow: 'none',
                                    borderRadius: '5px'
                                  }
                                }
                              }}
                              startAdornment={
                                <InputAdornment position="start" style={{ paddingBottom: 3, fontSize: 14 }}>
                                  {invoiceCalculation && invoiceCalculation.tds ? Utils.formatNumber(invoiceCalculation.tds.totalAmount || 0, true, true) : 0} |
                                </InputAdornment>
                              }
                              style={{ width: '100%' }}
                              value={tdsSelected._id ?? ''}
                              displayEmpty
                              onChange={onChangeTdsHandler}
                              // onChange={(e) => {
                              //   console.log(e);
                              //   setTcsSelected(tcsTaxType: e.target.value)
                              //   setCalculations({...calculations,tcsTaxType: e.target.value});
                              //   // const s = {...invoice};
                              //   // s.tcs.taxType = e.target.label;
                              //   // setInvoice(s);
                              // }}
                              size='small'
                              variant='outlined'
                              sx={{
                                boxShadow: "none",
                                // fontSize:12,
                                textAlign: 'right',
                                '& .MuiOutlinedInput-input': { p: 0.5 },
                                // '& .MuiSelect-select': { p:0 },
                                marginBottom: '3px',
                                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                {
                                  border: 0,
                                },
                                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  border: 0,
                                },
                              }}
                            >
                              <MenuItem value="">None</MenuItem>
                              {tdsTaxRateOptions && tdsTaxRateOptions.map((option) =>
                                <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>
                              )}
                            </Select>
                          </Grid>
                          {/* <Grid item xs={6} mb={2}></Grid> */}
                        </>
                        : ''}

                      <Grid item xs={12}>
                        <Divider sx={{ mb: 1 }} />
                      </Grid>

                      <Grid item xs={8} >
                        <h4 style={{ fontSize: 16, fontWeight: 500, marginBottom: 10 }}>Total Amount</h4>
                      </Grid>
                      <Grid item xs={4} style={{ textAlign: 'end' }}>
                        <h4 style={{ fontSize: 16, fontWeight: 500, marginBottom: 10 }}>{Utils.formatNumber((invoiceCalculation.totalAmount || 0) - Number(additionalChargesSum || 0), true, true)}</h4>
                      </Grid>

                      {invoice.additionalCharges.charges.length > 0 && invoice.additionalCharges.apply === true ? <>
                        <Grid item xs={12} >
                          <HighlightOffTwoToneIcon onClick={() => {
                            // setAdditionalCharges({...additionalCharges, apply:false, charges: []})
                            const _s = { ...invoice };
                            const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                            s.additionalCharges.apply = false;
                            s.additionalCharges.charges = [];
                            setInvoice(s);
                          }} sx={{ cursor: 'pointer', float: 'right', marginTop: '-2px', marginRight: -0.8, color: '#9da7b7', fontSize: 20 }} />
                          <Box sx={{ p: 1.5, pt: 1, pb: 1, border: '1px dashed grey', borderRadius: '10px', marginTop: '5px', marginBottom: 2 }} >
                            {invoice.additionalCharges.charges.map((record: any, index: number) =>
                              <React.Fragment key={record.id}>
                                <Grid container spacing={1} mb={1}>
                                  <Grid item xs={12} sm={12} >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={record.isTaxable || false}
                                          onChange={(e) => {
                                            const _s = { ...invoice };
                                            const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                                            s.additionalCharges.charges.forEach((m: any, i: number) => {
                                              if (m.id === record.id) {
                                                const isTaxable = e.target.checked;

                                                // Keep base amount unchanged
                                                const baseAmount = m.value;

                                                const taxPercentage = isTaxable ? (m.taxPercentage || 0) : 0;
                                                const taxAmount = isTaxable ? (baseAmount * taxPercentage) / 100 : 0;
                                                const totalWithTax = baseAmount + taxAmount;

                                                s.additionalCharges.charges[i] = {
                                                  ...m,
                                                  isTaxable: isTaxable,
                                                  taxPercentage: taxPercentage,
                                                  taxAmount: taxAmount,
                                                  totalWithTax: totalWithTax
                                                };
                                              }
                                            })
                                            setInvoice(s);
                                          }}
                                          size="small"
                                          sx={{ padding: '2px 9px' }}
                                        />
                                      }
                                      label={<span style={{ fontSize: '13px', color: '#666' }}>With Tax</span>}
                                      sx={{ margin: 0 }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={7}>
                                    <h5 className='c-label' style={{ marginBottom: 4 }}>Accounting Ledger *</h5>
                                    <Autocomplete
                                      disablePortal
                                      id={`charge-ledger-${record.id}`}
                                      options={chargeLedgers}
                                      getOptionLabel={(option) => `${option.ledgerName}`}
                                      value={chargeLedgers.find(l => l._id === record.ledgerId) || null}
                                      onChange={(event, newValue) => {
                                        const _s = { ...invoice };
                                        const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                                        s.additionalCharges.charges.forEach((m: any, i: number) => {
                                          if (m.id === record.id) {
                                            s.additionalCharges.charges[i] = {
                                              ...m,
                                              ledgerId: newValue?._id || '',
                                              ledgerName: newValue?.ledgerName || '',
                                              accountingCode: newValue?.accountingCode || ''
                                            };
                                          }
                                        })
                                        setInvoice(s);
                                      }}
                                      renderOption={(props, option: any) => (
                                        <li {...props} style={{ fontSize: 13, height: 'auto', padding: '8px' }}>
                                          <Box>
                                            <Typography variant="body2" fontWeight="bold" fontSize={13}>
                                              {option.ledgerName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontSize={11}>
                                              {option.primaryGroup?.name}
                                            </Typography>
                                          </Box>
                                        </li>
                                      )}
                                      classes={{ paper: classes.paper }}
                                      sx={{ width: '100%' }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          placeholder="Select ledger"
                                          required
                                          className='c-text-field'
                                          variant="standard"
                                          size='small'
                                          error={!record.ledgerId && invoice.additionalCharges.apply}
                                          InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true
                                          }}
                                        />
                                      )}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4.5}>
                                    <h5 className='c-label' style={{ marginBottom: 4 }}>Amount *</h5>
                                    <TextField
                                      className='c-text-field'
                                      variant="standard"
                                      size='small'
                                      type='number'
                                      value={record.value}
                                      required
                                      style={{ width: '100%' }}
                                      onChange={(p) => {
                                        const _s = { ...invoice };
                                        const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                                        s.additionalCharges.charges.forEach((m: any, i: number) => {
                                          if (m.id === record.id) {
                                            const newValue = parseFloat(p.target.value) || 0;
                                            const taxPercentage = m.taxPercentage || 0;
                                            const taxAmount = m.isTaxable ? (newValue * taxPercentage) / 100 : 0;
                                            const totalWithTax = newValue + taxAmount;
                                            s.additionalCharges.charges[i] = {
                                              ...m,
                                              value: newValue,
                                              taxAmount: taxAmount,
                                              totalWithTax: totalWithTax
                                            };
                                          }
                                        })
                                        setInvoice(s);
                                      }}
                                      InputProps={{
                                        disableUnderline: true,
                                        startAdornment: <InputAdornment position="start"><b style={{ paddingBottom: 3, fontSize: 14 }}>₹</b></InputAdornment>,
                                      }}
                                    />
                                  </Grid>


                                  {/* Tax fields - shown only when isTaxable is checked */}
                                  {record.isTaxable && (
                                    <>
                                      <Grid item xs={12} sm={4}>
                                        <h5 className='c-label' style={{ marginBottom: 4 }}>Tax Rate *</h5>
                                        <Select
                                            disableUnderline
                                            className='c-text-field'
                                            variant="standard"
                                            size='small'
                                            value={record.taxPercentage || 0}
                                            onChange={(e) => {
                                                const _s = { ...invoice };
                                                const s = JSON.parse(JSON.stringify(_s));
                                                // Added types (m: any, i: number) below to fix TS errors
                                                s.additionalCharges.charges.forEach((m: any, i: number) => {
                                                    if (m.id === record.id) {
                                                        // Added 'as string' cast
                                                        const newTaxPercentage = parseFloat(e.target.value as string) || 0;

                                                        // Keep the base amount unchanged
                                                        const baseAmount = m.value;

                                                        // Calculate new tax amount and total based on the new tax percentage
                                                        const taxAmount = m.isTaxable ? (baseAmount * newTaxPercentage) / 100 : 0;
                                                        const totalWithTax = baseAmount + taxAmount;

                                                        s.additionalCharges.charges[i] = {
                                                            ...m,
                                                            taxPercentage: newTaxPercentage,
                                                            taxAmount: taxAmount,
                                                            totalWithTax: totalWithTax
                                                        };
                                                    }
                                                })
                                                setInvoice(s);
                                            }}
                                            style={{ width: '100%' }}
                                        >
                                            {gstOptions && gstOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.lable}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Grid>
                                      <Grid item xs={12} sm={3.5}>
                                        <h5 className='c-label' style={{ marginBottom: 4 }}>Tax Amount</h5>
                                        <TextField
                                          className='c-text-field'
                                          variant="standard"
                                          size='small'
                                          value={record.taxAmount?.toFixed(2) || '0.00'}
                                          style={{ width: '100%' }}
                                          InputProps={{
                                            disableUnderline: true,
                                            readOnly: true,
                                            startAdornment: <InputAdornment position="start"><b style={{ paddingBottom: 3, fontSize: 14 }}>₹</b></InputAdornment>,
                                          }}
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        <h5 className='c-label' style={{ marginBottom: 4 }}>Final Amount</h5>
                                        <TextField
                                          className='c-text-field'
                                          variant="standard"
                                          size='small'
                                          value={record.totalWithTax?.toFixed(2) || '0.00'}
                                          style={{ width: '100%' }}
                                          InputProps={{
                                            disableUnderline: true,
                                            readOnly: true,
                                            startAdornment: <InputAdornment position="start"><b style={{ paddingBottom: 3, fontSize: 14 }}>₹</b></InputAdornment>,
                                          }}
                                        />
                                      </Grid>
                                    </>
                                  )}

                                  <Grid item xs={12} sm={0.5} display="flex" alignItems="flex-end">
                                    <HighlightOffTwoToneIcon
                                      sx={{ marginBottom: '4px', cursor: 'pointer', color: '#9da7b7', fontSize: 20 }}
                                      onClick={() => {
                                        const _s = { ...invoice };
                                        const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                                        s.additionalCharges.charges = s.additionalCharges.charges.filter((a: any) => a.id !== record.id);
                                        setInvoice(s);
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </React.Fragment>
                            )}
                            {invoice.additionalCharges.charges.length > 3 ? '' :
                              <h4 className="c-label" style={{ color: '#488dff', fontSize: 14, fontWeight: 500, marginTop: 5 }} >
                                <a onClick={() => {
                                  if (invoice.additionalCharges.charges.length > 4) { } else {
                                    const _s = { ...invoice };
                                    const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                                    s.additionalCharges.charges = [...s.additionalCharges.charges, {
                                      id: s.additionalCharges.charges[s.additionalCharges.charges.length - 1].id + 1,
                                      ledgerId: "",
                                      ledgerName: "",
                                      accountingCode: "",
                                      value: 0,
                                      isTaxable: false,
                                      taxPercentage: 0,
                                      taxAmount: 0,
                                      totalWithTax: 0,
                                    }];
                                    setInvoice(s);
                                  }
                                }} style={{ cursor: 'pointer' }}><AddCircleOutlineOutlinedIcon style={{ marginBottom: -2.8, fontSize: '16px' }} /> Add Another Charge</a>
                              </h4>}
                          </Box></Grid></> : <>
                        <Grid item xs={8}>
                          <h4 className="c-label" style={{ color: '#488dff', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>
                            <a onClick={() => {
                              const _s = { ...invoice };
                              const s = JSON.parse(JSON.stringify(_s)) as typeof _s;
                              s.additionalCharges.apply = true;
                              s.additionalCharges.charges = [{
                                id: 1,
                                ledgerId: "",
                                ledgerName: "",
                                accountingCode: "",
                                value: 0,
                                isTaxable: false,
                                taxPercentage: 0,
                                taxAmount: 0,
                                totalWithTax: 0,
                              }];
                              setInvoice(s);
                            }} style={{ cursor: 'pointer' }}><AddCircleOutlineOutlinedIcon style={{ marginBottom: -2.8, fontSize: '16px' }} /> Add Addtional Charges</a>
                          </h4>
                        </Grid>
                        <Grid item xs={4} style={{ textAlign: 'end' }}>
                          <span>₹ 0</span>
                        </Grid>
                      </>}

                      {(invoiceCalculation.totalPayableAmount > 0 && invoice.tds.apply === true) &&
                        <>
                          <Grid item xs={10} >
                            <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Total Payable Amount After TDS</h4>
                          </Grid>
                          <Grid item xs={2} style={{ textAlign: 'end' }}>
                            <h4 style={{ fontSize: 16, fontWeight: 500, marginBottom: 10 }}>{Utils.formatNumber(invoiceCalculation.totalPayableAmount || 0, true, true)}</h4>
                          </Grid>
                        </>
                      }

                      {invoiceCalculation.roundOff !== undefined && invoiceCalculation.roundOff !== 0 &&
                        <>
                          <Grid item xs={8} >
                            <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Round Off</h4>
                          </Grid>
                          <Grid item xs={4} style={{ textAlign: 'end' }}>
                            <span style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>{Utils.formatNumber(invoiceCalculation.roundOff, true, true)}</span>
                          </Grid>
                        </>
                      }

                      {invoiceCalculation.finalAmount > 0 &&
                        <>
                          <Grid item xs={8} >
                            <h4 style={{ fontSize: 16, fontWeight: 500, marginBottom: 10, color: '#1976d2' }}>Final Amount</h4>
                          </Grid>
                          <Grid item xs={4} style={{ textAlign: 'end' }}>
                            <h4 style={{ fontSize: 16, fontWeight: 500, marginBottom: 10, color: '#1976d2' }}>₹ {invoiceCalculation.finalAmount.toLocaleString('en-IN')}</h4>
                          </Grid>
                        </>
                      }

                      {(voucherType == VocuherTypes.ESTIMATE || voucherType == VocuherTypes.PURCHASE_ORDER) ? '' :
                        <>
                          <Grid item xs={12} style={{ textAlign: 'end' }}>
                            <Divider sx={{ mb: 1 }} />
                            <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}> {isNoteVoucher ? 'Mark as fully refunded' : 'Mark as fully paid'}</label>
                            <ToggleButton
                              value="check"
                              size='small' sx={{ ml: 1 }}
                              className={classes.toggleButton}
                              selected={invoice.fullyPaid}
                              onChange={() => {
                                const s = { ...invoice };
                                s.fullyPaid = !invoice.fullyPaid;
                                setSplitPayments([]);
                                if (s.fullyPaid) {
                                  var _recAmt = balanceAmount + receivedAmount;
                                  s.receivedAmount = _recAmt;
                                  setReceivedAmount(Number(_recAmt));
                                } else {
                                  s.receivedAmount = 0;
                                  setReceivedAmount(0);
                                }
                                setInvoice(s)
                              }}
                            >
                              {!invoice.fullyPaid ? '' : <CheckIcon style={{ fontSize: 15 }} />}
                            </ToggleButton>

                          </Grid>

                          <Grid item xs={5} mt={1} pt={1}>
                            <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>{isNoteVoucher ? (type == VocuherCategory.SALE ? 'Refund Paid' : 'Refund Received') : `Amount ${type == VocuherCategory.SALE ? 'Received' : 'Paid'}`}</h4>
                          </Grid>
                          <Grid item xs={7} mt={1} style={{ textAlign: 'end' }}>
                            {/* <span>₹ 0</span> */}
                            <TextField
                              className='c-text-field number-right'
                              variant="standard"
                              size='small'
                              style={{ width: '100%' }}
                              // value={receivedAmount}
                              value={Utils.toFixedWithoutZeros(receivedAmount, 2)}
                              type="number"
                              disabled={splitPayments.length > 0}
                              onWheel={(e) => (e.target as HTMLInputElement).blur()}
                              onChange={(e) => {
                                // No negatives; paying the final amount or more marks it fully paid.
                                const finalAmt = Number(invoiceCalculation.finalAmount) || 0;
                                const value = Math.min(Math.max(0, Number(e.target.value) || 0), finalAmt);
                                const s = { ...invoice, fullyPaid: finalAmt > 0 && value >= finalAmt };
                                s.receivedAmount = Utils.toFixedWithoutZeros(value, 2);
                                setInvoice(s);
                                setReceivedAmount(value);
                              }}
                              InputProps={{
                                disableUnderline: true,
                                style: {
                                  fontSize: 16,
                                  height: 35
                                },
                                startAdornment: <InputAdornment position="start">
                                  <Select
                                    className='c-text-field-2'
                                    value={splitPayments.length ? 'SPLIT' : paymentMode}
                                    disabled={splitPayments.length > 0}
                                    disableUnderline
                                    MenuProps={{
                                      PaperProps: {
                                        sx: {
                                          border: "1.5px solid #e9ecf1",
                                          width: 150,
                                          boxShadow: 'none',
                                          borderRadius: '5px'
                                        }
                                      }
                                    }}
                                    style={{ width: 150 }}
                                    size='small'
                                    variant='outlined'
                                    onChange={(e) => {
                                      // const s = { ...invoice };
                                      // s.receivedAmount = Utils.toFixedWithoutZeros(Number(e.target.value), 2);
                                      // setInvoice(s);
                                      setPaymentMode(String(e.target.value).toUpperCase());
                                    }}
                                    sx={{
                                      boxShadow: "none",
                                      fontSize: 14,
                                      height: 28,
                                      textAlign: 'left',
                                      '& .MuiOutlinedInput-input': { p: 0 },
                                      '& .MuiSelect-select': { p: 0 },
                                      marginBottom: '2px',
                                      ".MuiOutlinedInput-notchedOutline": { border: 0 },
                                      "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        border: 0,
                                      },
                                      "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        border: 0,
                                      },
                                    }}
                                  >
                                    <MenuItem value={'UPI'} >UPI</MenuItem>
                                    <MenuItem value={'CASH'} selected>Cash</MenuItem>
                                    <MenuItem value={'CARD'}>Card</MenuItem>
                                    <MenuItem value={'CHEQUE'}>Cheque</MenuItem>
                                    <MenuItem value={'NETBANKING'}>Net Banking</MenuItem>
                                    {splitPayments.length > 0 && <MenuItem value={'SPLIT'}>Split</MenuItem>}
                                  </Select><b style={{ paddingBottom: 3, fontSize: 14 }}>&nbsp;&nbsp;&nbsp; ₹</b>
                                </InputAdornment>,
                              }}
                            />
                          </Grid>
                          {splitPayments.length > 0 ? <Grid item xs={12} mt={1}>
                            <Box sx={{ border: '1px solid #e9ecf1', borderRadius: '8px', p: 1.5 }}>
                              <h4 style={{ fontSize: 13, fontWeight: 500, color: '#9da7b7', marginBottom: 6 }}>PAYMENT SPLIT</h4>
                              {splitPayments.map((p, i) => (
                                <Grid container key={i} sx={{ fontSize: 14, py: 0.5 }}>
                                  <Grid item xs={3}><b style={{ fontWeight: 500 }}>{p.paymentMode === 'NETBANKING' ? 'Net Banking' : p.paymentMode === 'CASH' ? 'Cash' : p.paymentMode === 'CHEQUE' ? 'Cheque' : p.paymentMode === 'CARD' ? 'Card' : p.paymentMode}</b></Grid>
                                  <Grid item xs={5} sx={{ color: '#9da7b7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.instrumentId || '—'}</Grid>
                                  <Grid item xs={4} textAlign="end">{Utils.formatNumber(p.amount, true, true)}</Grid>
                                </Grid>
                              ))}
                            </Box>
                          </Grid> : <>
                          <Grid item xs={5} mt={1} pt={1}>
                            <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Cheque Number or UPI Id or Trxn ID</h4>
                          </Grid>
                          <Grid item xs={7} mt={1} style={{ textAlign: 'end' }}>
                            <TextField
                              placeholder='Reference Number'
                              onChange={(e) => setInvoiceText(e.target.value.toString(), 'instrumentId')}
                              className='c-text-field'
                              variant="standard"
                              size='small'
                              // disabled={paymentMode == 'CASH' ? true : false}
                              InputProps={{
                                disableUnderline: true,
                              }}
                            />
                          </Grid></>}

                          {<Grid item xs={12} mt={1} style={{ textAlign: 'end' }}>
                            <span style={{ fontSize: 13, color: '#1976d2', cursor: 'pointer' }} onClick={() => {
                              // Snapshot the single payment only, so Cancel always returns to it.
                              if (!splitPayments.length) splitSnapshot.current = { splitPayments, receivedAmount, paymentMode, fullyPaid: invoice.fullyPaid, invoiceReceived: invoice.receivedAmount };
                              setSplitDialogOpen(true);
                            }}>
                              {splitPayments.length ? 'Edit split payment' : '+ Split payment'}
                            </span>
                          </Grid>}

                          <SplitPaymentDialog
                            open={splitDialogOpen}
                            payableAmount={Number(invoiceCalculation.finalAmount) || 0}
                            lines={splitPayments}
                            defaultMode={paymentMode}
                            defaultAmount={receivedAmount}
                            defaultReference={invoice.instrumentId}
                            onClose={() => {
                              const snap = splitSnapshot.current;
                              if (snap) {
                                setSplitPayments(snap.splitPayments);
                                setReceivedAmount(snap.receivedAmount);
                                setPaymentMode(snap.paymentMode);
                                setInvoice((prev: any) => ({ ...prev, fullyPaid: snap.fullyPaid, receivedAmount: snap.invoiceReceived }));
                              }
                              setSplitDialogOpen(false);
                            }}
                            onChange={(lines) => {
                              const total = Number(lines.reduce((sum, l) => sum + l.amount, 0).toFixed(2));
                              setSplitPayments(lines);
                              setReceivedAmount(total);
                              setInvoice((prev: any) => ({ ...prev, fullyPaid: false, receivedAmount: total }));
                            }}
                            onSaveShare={(canSaveShare || canUpdateShare) ? (lines) => { shareAfterSaveRef.current = true; onSplitSave(lines); } : undefined}
                            onSave={(lines) => { shareAfterSaveRef.current = false; onSplitSave(lines); }}
                            saveLabel={isEditing ? `Update ${pageTitle}` : 'Save & View'}
                          />

                          <Grid item xs={12} mt={2}>
                            <Divider />
                          </Grid>

                          <Grid item xs={8} mt={1}>
                            <h4 style={{ fontSize: 16, fontWeight: 500, marginBottom: 10, color: '#059669' }}>Balance Amount</h4>
                          </Grid>
                          <Grid item xs={4} mt={1} style={{ textAlign: 'end' }}>
                            <span style={{ fontSize: 16, fontWeight: 500, marginBottom: 10, color: '#059669' }}>{Utils.formatNumber(balanceAmount || 0, true, true)}</span>
                          </Grid>
                        </>
                      }

                      < Grid item xs={12} style={{ textAlign: 'end' }}>
                        <Divider sx={{ mb: 2 }} />
                        <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Authorized signatory for <span style={{ fontSize: 18 }}>{selectedAccount.name}</span></h4>

                        <img style={{ width: 250 }} src={selectedAccount.signature} />
                      </Grid>



                    </Grid>

                  </Grid>
                  {/* <InvoiceButtons type={type} selectedSend={selectedDispatchHandler} invoiceStatus={invoiceStatus} onCancel={onCancel} onSubmit={onSubmit} /> */}
                </Grid>
              </Box>
              {/* </TableContainer> */}

              {/* <StyledCard sx={{ pb: 5 }}>
      <FormProvider {...methods}>
        <CardContent className={'invoiceContent'}>
          <Box component="form" onSubmit={methods.handleSubmit(data => onSubmit(data, false))} ref={formRef} noValidate sx={{ mt: 1 }} style={{ position: 'relative' }}>
            {isformLoaded && <StyledBoxLoader component="div" sx={{ mt: 1 }}><CircularProgress color="inherit" /></StyledBoxLoader>}
            <LocalizationProvider dateAdapter={AdapterDateFns}> */}
              {/* <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }} style={{margin:"-15px"}}>
                <Grid item xs={2} sm={4} md={4} key={1}>
                  <Item>
                    <Chip label="Invoice Details"  style={{float: 'right', marginBottom: '20px', fontWeight:'600', backgroundColor:"#183f58", color:"white" }}/>
                    
                    
                    {(type === 'account-receivable') && <div style={{ display: 'flex', marginTop:'40px', marginBottom:'20px', width:"100%" }}>
                      <Avatar
                        alt={selectedAccount.name || 'Picture'}
                        imgProps={{ "referrerPolicy": 'no-referrer' }}
                        src={selectedAccount.logo}
                        // variant='square'
                        style={{ width: '50px', height: '50px', objectFit: 'contain', marginRight: '10px', borderRadius: '50%', marginTop: '15px'  }}
                      />
                        {isLoading ? <div></div> : <CustomTextField className={'disabledField'} label="Supplier" variant="outlined" name="account" InputProps={{ readOnly: true, disabled: true }} />}
                      </div>
                    }
                    {(type === 'account-payable') && <>
                      <Stack style={{ flexDirection: 'row', alignItems: 'center', marginTop: '24px' }}>
                        <Avatar
                          alt={selectedClient?.name || ''}
                          imgProps={{ "referrerPolicy": 'no-referrer' }}
                          src={selectedClient?.logo || ''}
                          // variant='square'
                          style={{ width: '50px', height: '50px', objectFit: 'contain', marginRight: '10px', borderRadius: '50%', marginTop: '5px'  }}
                        />
                        {isClientLoading ? <div></div> : <CustomTextField className={'disabledField'} variant="standard" label={"Supplier"} name="client" placeholder={'Needs to be selected from drawer'} InputProps={{ readOnly: true, disabled: true }} />}
                        <Tooltip title="Add Supplier"><SettingsIcon style={{ cursor: 'pointer' }} onClick={() => { openCloseDrawer('client') }} fontSize="small" /></Tooltip>
                      </Stack>
                    </>}

                    {isLoading ? <div></div> : <CustomTextField variant="outlined" size='small' label="Invoice" name="invoiceNumber" required InputProps={{ readOnly: (type === 'account-receivable' || (isEditing && type === 'account-payable')) ? true : false, disabled: (type === 'account-receivable' || (isEditing && type === 'account-payable')) ? true : false }} className={(type === 'account-receivable') ? 'disabledField' : ''} />}
                    <DesktopDatePicker
                      label="Invoice Date"
                      inputFormat="dd/MM/yyyy"
                      maxDate={moment().add(1).toDate()}
                      value={valueDate}
                      onChange={(value) => { handleDateChange(value, 'valueDate') }}
                      renderInput={(params) => <TextField {...params} name={'valueDate'} required variant="outlined" size='small' style={{ marginRight: (type === 'account-receivable') ? '10px' : '0px', flex: 1, marginTop: '16px', marginBottom: '8px', flexDirection: 'row', alignItems: 'center'}}/>}
                    />
                  </Item>
                </Grid>
                <Grid item xs={2} sm={4} md={4} key={2}>
                  <Item>
                    <Chip label="Buyer Details"  style={{float: 'right', marginBottom: '20px', fontWeight:'600', backgroundColor:"#183f58", color:"white" }}/>
                    {(type === 'account-receivable') && <>
                      <Stack style={{ flexDirection: 'row', alignItems: 'center', marginTop:'40px', marginBottom:'0px', width:"100%" }}>
                        <Avatar
                          alt={selectedClient?.name || ''}
                          imgProps={{ "referrerPolicy": 'no-referrer' }}
                          src={selectedClient?.logo || ''}
                          // variant='circle'
                          style={{ width: '50px', height: '50px', objectFit: 'contain', marginRight: '10px', borderRadius: '50%', marginTop: '5px'  }}
                        />
                        {isClientLoading ? <div></div> : <CustomTextField className={'disabledField'} variant="outlined" label={"Buyer"} name="client" required placeholder={'Needs to be selected from drawer'} InputProps={{ readOnly: true, disabled: true }} />}

                        <Tooltip title="Search/Add Buyer">
                          <PersonSearchRounded 
                            style={{ cursor: 'pointer', color: '#591ebb', backgroundColor: '#babbff7d', width:'40px', height:'40px', padding:'5px', borderRadius: '30px', marginLeft: '10px'}} 
                            onClick={() => { openCloseDrawer('buyer') }} 
                            fontSize="small" /></Tooltip>
                      </Stack>
                    </>}
                    <Stack style={{ flexDirection: 'row', alignItems: 'center', marginTop: '0px' }}>
                      <Avatar
                        alt={selectedAgent?.name || 'Picture'}
                        imgProps={{ "referrerPolicy": 'no-referrer' }}
                        src={selectedAgent?.logo || ''}
                        style={{ width: '50px', height: '50px', objectFit: 'contain', marginRight: '10px', borderRadius: '50%', marginTop: '5px'  }}
                        // variant='square'
                      />
                      {isAgentLoading ? <div></div> : <CustomTextField className={'disabledField'} variant="outlined" label="Agent" name="agent" placeholder={'Needs to be selected from drawer'} InputProps={{ readOnly: true, disabled: true }} />}
                      <Tooltip title="Add Agent">
                        <PersonSearchRounded 
                            style={{ cursor: 'pointer', color: '#591ebb', backgroundColor: '#babbff7d', width:'40px', height:'40px', padding:'5px', borderRadius: '30px', marginLeft: '10px'}} 
                            onClick={() => { openCloseDrawer('agent') }} 
                            fontSize="small" />
                      </Tooltip>
                    </Stack>

                  {(type === 'account-payable') &&
                    <div style={{ display: 'flex', marginTop: '24px' }}>
                      <Avatar
                        alt={selectedAccount.name || 'Picture'}
                        imgProps={{ "referrerPolicy": 'no-referrer' }}
                        src={selectedAccount.logo}
                        // variant='square'
                        style={{ width: '50px', height: '50px', objectFit: 'contain', marginRight: '10px', borderRadius: '50%', marginTop: '5px'  }}
                      />
                      {isLoading ? <div></div> : <CustomTextField className={'disabledField'} label="Buyer" variant="standard" name="account" required InputProps={{ readOnly: true, disabled: true }} />}
                    </div>
                  }

                  {isClientLoading || isBuyerLoading ? <div></div> : <CustomTextField className={'disabledField'} style={{ marginTop: '24px' }} variant="outlined" multiline label="Bill To" name="billingAddress" required InputProps={{ readOnly: true, disabled: true }} />}

                  {isClientLoading || isBuyerLoading ? <div></div> : <CustomTextField className={'disabledField'} style={{ marginTop: '24px' }} variant="outlined" multiline label="Ship To" name="shippingAddress" required InputProps={{ readOnly: true, disabled: true }} />}

                  
                      
                  </Item>
                </Grid>
                
                <Grid item xs={2} sm={4} md={4} key={3}>
                  <Item style={{color:'black'}}>
                    <Chip label="Work Order Details"  style={{float: 'right', marginBottom: '20px', fontWeight:'600', backgroundColor:"#183f58", color:"white" }}/>
                    <Grid container spacing={4} xs={12} sm={12} md={12} style={{margin: "0px", padding: "0px", marginBottom:'20px', width:"100%"}}>
                        <Grid item xs={12} sm={12} md={6} style={{padding:'5px'}}>
                          <DesktopDatePicker
                            label="Quotation Date"
                            inputFormat="dd/MM/yyyy"
                            value={quotationDate}
                            onChange={(value) => { handleDateChange(value, 'quotationDate') }}
                            renderInput={(params) => <TextField {...params} name={'quotationDate'} required variant="outlined" size='small'/>}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} style={{padding:'5px'}}>
                          <CustomTextField style={{ margin: '0px 0px' }} variant="outlined" label="Quotation #" name="quotationNumber" size='small' />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} style={{padding:'5px'}}>
                          <DesktopDatePicker
                            label="PO Date"
                            inputFormat="dd/MM/yyyy"
                            value={poDate}
                            onChange={(value) => { handleDateChange(value, 'poDate') }}
                            renderInput={(params) => <TextField {...params} name={'poDate'} required variant="outlined" size='small' />}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} style={{padding:'5px'}}>
                          <CustomTextField style={{ margin: '0px 0px' }} variant="outlined" size='small' label="PO #" name="poNumber" />
                        </Grid>
                        <Grid item xs={12} style={{padding:'5px'}}>
                          <CustomTextField style={{ margin: '0px 0px' }} variant="outlined" size='small' label="Ref 1" name="ref1" />
                        </Grid>
                        <Grid item xs={12} style={{padding:'5px'}}>
                          <CustomTextField style={{ margin: '0px 0px' }} variant="outlined" size='small' label="Ref 2" name="ref2" />
                        </Grid>
                        <Grid item xs={12} style={{padding:'5px'}}>
                          <CustomTextField style={{ margin: '5px 0px' }} variant="outlined" size='small' label="Payment term" name="paymentTerms" />
                        </Grid>
                        <Grid item xs={12} style={{padding:'5px', paddingTop:"0px"}}>
                          {stateOptions && <CustomSelect
                              name="placeOfSupply"
                              label="Place of Supply"
                              id="state"
                              variant="outlined"
                              size='small'
                              options={stateOptions}
                              // value={selectedState}
                              value={placeOfSupply ?? ''}
                              placeholder="Select Place of supply"
                              // onChange={(event: SelectChangeEvent<any>) => { handleChange(event) }} 

                              onChange={(event: SelectChangeEvent<any>) => {
                                methods.setValue("placeOfSupply", event.target.value);
                                setPlaceOfSupply(event.target.value);
                              }}
                            />
                          }
                        </Grid>
                        
                    </Grid>
                    
                  </Item>
                </Grid>
              </Grid> */}

              {/* <TableInlineEdit onChangeRow={rowOnChangeHandler} rowsData={editInLineItems} /> */}

              {/* <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}> */}
              {/* <Grid item xs={2} sm={4} md={5} key={1}>
                  <Item>
                    <DesktopDatePicker
                      label="Pay by Date"
                      inputFormat="dd/MM/yyyy"
                      value={payByDate}
                      onChange={(value) => { handleDateChange(value, 'payBydate') }}
                      renderInput={(params) => <TextField {...params} name={'payBydate'} required variant="standard" />} 
                    />
                    
                    {(type === 'account-receivable') && <>
                      {isLoading ? <div></div> : <CustomTextField variant="standard" multiline label="Terms & Conditions" name="terms" />}
                      {isLoading ? <div></div> : <CustomTextField variant="standard" multiline label="Footer" name="footer1" />}
                        
                    </>}
                    </Item>
                </Grid> */}
              {/* <Grid item xs={2} sm={4} md={4} key={2}>
                  <Item>
                    {isLoading ? <div></div> : <> <PayTo onChangePayTo={payToOnChangeHandler} account={payToAccount} payToObj={payToObj} />
                      <CustomTextField style={{ margin: '5px 0px', display: 'none' }} variant="standard" label="Payment terms" name="paymentTerms1" /></>}
                      <TemplateSelect invoiceId={invoiceId} invoiceStatus={invoiceStatus} invoiceDocType={'sale-invoice'}/>
                      
                  </Item>
                </Grid> */}

              {/* <Grid item xs={2} sm={4} md={3} key={3}>
                  <Item style={{color:'black'}}>
                    <CustomTextField className={`disabledField text-right`} label="Taxable Amount" variant="standard" name="taxAmount" InputProps={{ readOnly: true, disabled: true, size: 'small'}} />
                    <CustomTextField className={`disabledField text-right`} label="IGST" variant="standard" name="tax3Format" InputProps={{ readOnly: true, disabled: true, size: 'small' }} /><CustomTextField style={{ display: 'none' }} className={'hiddenText'} variant="standard" label="" name="tax3" />
                    <CustomTextField className={`disabledField text-right`} label="CGST" variant="standard" name="tax2Format" InputProps={{ readOnly: true, disabled: true, size: 'small' }} /><CustomTextField style={{ display: 'none' }} className={'hiddenText'} variant="standard" label="" name="tax2" />
                    <CustomTextField className={`disabledField text-right`} label="SGST" variant="standard" name="tax1Format" InputProps={{ readOnly: true, disabled: true, size: 'small' }} /><CustomTextField style={{ display: 'none' }} className={'hiddenText'} variant="standard" label="" name="tax1" />
                    <CustomTextField className={`disabledField text-right`} label="Total" variant="standard" name="amountFormat" required InputProps={{ readOnly: true, disabled: true, size: 'small' }} /><CustomTextField style={{ display: 'none' }} className={'hiddenText'} variant="standard" label="" name="amount" />
                  
                  </Item>
                </Grid> */}

              {/* </Grid>
            </LocalizationProvider> */}

              {/* <InvoiceButtons type={type} selectedSend={selectedDispatchHandler} invoiceStatus={invoiceStatus} onCancel={onCancel} /> */}
              {/* </Box>
        </CardContent> */}
              {/* {docData && <DocAttachInfo docList={docData} onDelete={docDelete} onClose={closeDocAttach} />} */}

              {/* </FormProvider> */}

              {/* Theme gives every Paper inside a Drawer a shadow; keep the preview's flat panels flat. */}
              <Drawer anchor="right" open={previewDrawerOpen} onClose={() => setPreviewDrawerOpen(false)}
                sx={{ '& .MuiPaper-elevation0': { boxShadow: 'none' } }}
                PaperProps={{ sx: { width: { xs: '100%', md: '95vw' }, bgcolor: '#fafafb', overflowX: 'hidden' } }}>
                <DialogTitle sx={greyDialogTitleSx}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: '40px' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>Previous Invoices</Typography>
                    <IconButton size="small" onClick={() => setPreviewDrawerOpen(false)}><CloseIcon /></IconButton>
                  </Box>
                </DialogTitle>
                <InvoicePreviewWithFilters embedded invoiceId={savedView.savedInvoiceId || invoiceId} templateId={savedView.savedTemplateId || (isEditing ? invoice.pdfTemplate : undefined)}
                  type={type === VocuherCategory.PURCHASE || type === 'account-payable' ? VocuherCategory.PURCHASE : VocuherCategory.SALE} />
              </Drawer>
              <Drawer anchor="right" open={shareDrawerOpen} onClose={() => setShareDrawerOpen(false)}>
                {shareData ? (
                  <DialogShareInvoiceReciept dispatchType={shareData.invoice?.voucherType} invoiceId={savedView.savedInvoiceId}
                    invoicePreviewData={shareData.preview} selectedBuyer={shareData.invoice?.client}
                    openDrawer={shareDrawerOpen} onClose={() => setShareDrawerOpen(false)} />
                ) : (
                  <Box sx={{ width: '50vw', p: 4, textAlign: 'center' }}>
                    <CircularProgress color="info" />
                    <Typography sx={{ mt: 2 }}>Preparing invoice PDF ...</Typography>
                  </Box>
                )}
              </Drawer>
              <Drawer anchor="right" open={open[0]}>
                {<DialogDocumentSettingForm open={open[0]} setDocumentType={'invoice'} documentSettingId={documentSettingId} accountId={selectedAccount._id} onClose={closeDialog}></DialogDocumentSettingForm>}
              </Drawer>
              <Drawer anchor="right" open={open[1]} >
                {<DialogClientForm open={open[1]} title={'Supplier'} onClose={closeDialog} onCancel={cancelDialog} selectedData={selectedClientHandler}></DialogClientForm>}
              </Drawer>
              <Drawer anchor="right" open={open[2]}>
                {<DialogClientForm open={open[2]} title={'Agent'} onClose={closeDialog} onCancel={cancelDialog} selectedData={selectedAgentHandler}></DialogClientForm>}
              </Drawer>
              {/* <Drawer anchor="right" open={open[3]} > */}
              {<DialogClientForm open={open[3]} title={'Buyer'} onClose={closeDialog} onCancel={cancelDialog} selectedData={selectedClientHandler} locationAddress={selectedLocation?.addressId}></DialogClientForm>}
              {/* </Drawer> */}
              {/* </StyledCard> */}
            </div>
          </Card>
        </>
      )
      }
    </>
  );
}

// New location key (e.g. after Save & View) remounts a clean form.
const InvoiceForm: FC<InvoiceProps> = (props) => <Invoice key={useLocation().key} {...props} />;

export default InvoiceForm;
